import { useState, useEffect } from 'react';
// ────────────────────────────────────────────────────────────────────────
// PACT.HEALTH · App.jsx
// Single-file build with login → coach dashboard or athlete app.
// All data comes from ./mockData for the demo.
// To switch back to live Supabase data: import from ./supabase queries.
// ────────────────────────────────────────────────────────────────────────
import {
  ATHLETES, COACH, CLIENT_DATA, PACTS, STAKES, COSIGNERS, GOALS,
  TODAYS_PACT, PAX_MESSAGES, RECENT_WINS, getCurrentPact,
} from './mockData';

// ════════════════════════ STYLES ════════════════════════
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@600;700;800;900&family=Inter:wght@400;500;600;700&family=Caveat:wght@600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --navy: #0A2540; --red: #D92D20; --red-light: #fef2f1;
    --white: #FFFFFF; --bg: #F4F6F8; --bg2: #EBF1F5; --border: #e2e8f0;
    --border2: #d0d9e3; --muted: #4A4A4A; --muted2: #7a8a9a;
    --amber: #D97706; --green: #059669; --green-soft: #ecfdf5;
    --whatsapp: #25D366; --whatsapp-dark: #075E54;
    --font: 'Inter', sans-serif; --head: 'Montserrat', sans-serif;
    --shadow-sm: 0 1px 4px rgba(10,37,64,0.06);
    --shadow-md: 0 4px 16px rgba(10,37,64,0.1);
    --shadow-lg: 0 8px 32px rgba(10,37,64,0.14);
    --transition: all 0.2s cubic-bezier(0.4,0,0.2,1);
  }
  html, body, #root { height: 100%; background: var(--bg); color: var(--navy); font-family: var(--font); font-size: 14px; }
  button { font-family: inherit; }
  input, textarea { font-family: inherit; }
  .num { font-variant-numeric: tabular-nums; }

  @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
  @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }
  @keyframes commitStamp { 0% { transform: scale(0) rotate(-12deg); opacity: 0; } 60% { transform: scale(1.1) rotate(-6deg); opacity: 1; } 100% { transform: scale(1) rotate(-6deg); opacity: 1; } }
  .animate-in { animation: fadeSlideUp 0.4s cubic-bezier(0.4,0,0.2,1) both; }
  .fade-in { animation: fadeIn 0.3s ease both; }
  .slide-in-right { animation: slideInRight 0.3s cubic-bezier(0.4,0,0.2,1) both; }
  .commit-stamp { animation: commitStamp 0.6s ease-out forwards; }
  .stagger-1 { animation-delay: 0.05s; }
  .stagger-2 { animation-delay: 0.10s; }
  .stagger-3 { animation-delay: 0.15s; }
  .stagger-4 { animation-delay: 0.20s; }
  .stagger-5 { animation-delay: 0.25s; }
  .stagger-6 { animation-delay: 0.30s; }
  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--border2); border-radius: 3px; }

  /* ═══ LOGIN ═══ */
  .login-screen {
    height: 100vh; display: flex; align-items: center; justify-content: center;
    background: var(--navy); padding: 24px;
  }
  .login-box { width: 100%; max-width: 460px; }
  .login-brand { display: flex; align-items: center; gap: 12px; margin-bottom: 40px; }
  .login-brand-mark { width: 44px; height: 44px; border-radius: 8px; background: var(--red); display: flex; align-items: center; justify-content: center; font-family: var(--head); font-weight: 900; font-size: 22px; color: white; }
  .login-brand-text { color: white; font-family: var(--head); font-weight: 800; font-size: 18px; letter-spacing: 0.02em; }
  .login-brand-text span { color: var(--red); }
  .login-eyebrow { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; }
  .login-eyebrow .bar { width: 4px; height: 14px; background: var(--red); border-radius: 2px; }
  .login-eyebrow .text { font-size: 10px; font-weight: 700; color: var(--red); text-transform: uppercase; letter-spacing: 0.2em; }
  .login-headline {
    font-family: var(--head); font-weight: 800; font-size: 44px;
    color: white; line-height: 0.95; letter-spacing: -0.025em; margin-bottom: 12px;
  }
  .login-headline span { color: var(--red); }
  .login-sub { color: rgba(255,255,255,0.7); font-size: 14px; line-height: 1.6; margin-bottom: 32px; }
  .login-options { display: flex; flex-direction: column; gap: 10px; }
  .login-card {
    padding: 18px 20px; background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.12); border-radius: 10px;
    cursor: pointer; transition: var(--transition); display: flex; align-items: center; gap: 14px;
  }
  .login-card:hover { background: rgba(255,255,255,0.08); border-color: rgba(255,255,255,0.25); transform: translateY(-2px); }
  .login-card-icon { width: 42px; height: 42px; border-radius: 8px; background: var(--red); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .login-card-icon.coach { background: rgba(255,255,255,0.1); }
  .login-card-content { flex: 1; }
  .login-card-role { font-size: 10px; font-weight: 700; color: rgba(255,255,255,0.5); text-transform: uppercase; letter-spacing: 0.18em; margin-bottom: 4px; }
  .login-card-name { color: white; font-family: var(--head); font-weight: 700; font-size: 16px; }
  .login-card-meta { color: rgba(255,255,255,0.55); font-size: 12px; margin-top: 2px; }
  .login-card-arrow { color: rgba(255,255,255,0.3); }
  .login-footer { margin-top: 32px; padding-top: 24px; border-top: 1px solid rgba(255,255,255,0.08); font-size: 11px; color: rgba(255,255,255,0.4); text-align: center; }

  /* ═══ COACH DASHBOARD (kept from original style) ═══ */
  .app { display: flex; height: 100vh; overflow: hidden; }
  .sidebar { width: 220px; background: var(--navy); display: flex; flex-direction: column; flex-shrink: 0; }
  .sidebar-logo { padding: 24px 20px; border-bottom: 1px solid rgba(255,255,255,0.08); margin-bottom: 8px; }
  .sidebar-logo .brand { font-family: var(--head); font-size: 18px; font-weight: 800; color: white; }
  .sidebar-logo .brand span { color: var(--red); }
  .sidebar-logo .tagline { font-size: 10px; color: rgba(255,255,255,0.4); margin-top: 3px; letter-spacing: 0.05em; text-transform: uppercase; }
  .nav-item { padding: 10px 20px; cursor: pointer; font-size: 13px; color: rgba(255,255,255,0.5); transition: var(--transition); display: flex; align-items: center; gap: 10px; border-left: 3px solid transparent; font-weight: 500; }
  .nav-item:hover { color: white; background: rgba(255,255,255,0.05); }
  .nav-item.active { color: white; border-left-color: var(--red); background: rgba(255,255,255,0.07); }
  .sidebar-bottom { margin-top: auto; padding: 16px 20px; border-top: 1px solid rgba(255,255,255,0.08); }
  .sidebar-logout { display: flex; align-items: center; gap: 8px; padding: 8px 0; color: rgba(255,255,255,0.4); font-size: 11px; cursor: pointer; transition: color 0.2s; }
  .sidebar-logout:hover { color: white; }
  .refresh-btn { width: 100%; padding: 8px 0; background: var(--red); border: none; color: white; border-radius: 6px; cursor: pointer; font-size: 12px; font-weight: 600; transition: var(--transition); margin-top: 8px; letter-spacing: 0.02em; }
  .refresh-btn:hover { background: #b82419; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(217,45,32,0.3); }

  .main { flex: 1; display: flex; flex-direction: column; overflow: hidden; }
  .topbar { display: flex; align-items: center; justify-content: space-between; padding: 16px 28px; border-bottom: 1px solid var(--border); background: var(--white); flex-shrink: 0; box-shadow: var(--shadow-sm); }
  .topbar-title { font-family: var(--head); font-size: 17px; font-weight: 700; color: var(--navy); }
  .topbar-sub { font-size: 12px; color: var(--muted2); margin-top: 1px; }
  .status-filters { display: flex; gap: 8px; }
  .status-pill { padding: 6px 14px; border-radius: 6px; font-size: 11px; font-weight: 600; cursor: pointer; border: 1px solid transparent; transition: var(--transition); text-transform: uppercase; letter-spacing: 0.04em; }
  .status-pill.all { border-color: var(--border2); color: var(--muted2); background: var(--white); }
  .status-pill.all.active, .status-pill.all:hover { border-color: var(--navy); color: var(--navy); background: var(--bg2); }
  .status-pill.push { border-color: rgba(10,37,64,0.2); color: var(--navy); background: var(--bg2); }
  .status-pill.push.active, .status-pill.push:hover { background: var(--navy); color: white; }
  .status-pill.caution { border-color: rgba(217,119,6,0.3); color: var(--amber); background: #fffbeb; }
  .status-pill.caution.active, .status-pill.caution:hover { background: var(--amber); color: white; }
  .status-pill.holdback { border-color: rgba(217,45,32,0.3); color: var(--red); background: var(--red-light); }
  .status-pill.holdback.active, .status-pill.holdback:hover { background: var(--red); color: white; }

  .content { flex: 1; display: grid; grid-template-columns: 1fr 320px; overflow: hidden; }
  .content-main { overflow-y: auto; padding: 24px 28px; }
  .content-side { border-left: 1px solid var(--border); overflow-y: auto; background: var(--white); }

  .summary-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 20px; }
  .summary-card { background: var(--white); border: 1px solid var(--border); border-radius: 10px; padding: 16px 18px; box-shadow: var(--shadow-sm); transition: var(--transition); position: relative; overflow: hidden; }
  .summary-card::after { content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 3px; transform: scaleX(0); transition: transform 0.3s ease; transform-origin: left; }
  .summary-card:hover { box-shadow: var(--shadow-md); transform: translateY(-2px); }
  .summary-card:hover::after { transform: scaleX(1); }
  .summary-card.navy::after  { background: var(--navy); }
  .summary-card.red::after   { background: var(--red); }
  .summary-card.amber::after { background: var(--amber); }
  .summary-card.green::after { background: var(--green); }
  .summary-label { font-size: 10px; color: var(--muted2); text-transform: uppercase; letter-spacing: 0.07em; font-weight: 600; margin-bottom: 8px; }
  .summary-val { font-family: var(--head); font-size: 26px; font-weight: 800; color: var(--navy); }
  .summary-sub { font-size: 11px; color: var(--muted2); margin-top: 2px; }

  .chart-section { background: var(--white); border: 1px solid var(--border); border-radius: 10px; padding: 20px; margin-bottom: 20px; box-shadow: var(--shadow-sm); }
  .section-label { font-family: var(--head); font-size: 13px; font-weight: 700; color: var(--navy); margin-bottom: 16px; display: flex; align-items: center; justify-content: space-between; }
  .athlete-chart { display: flex; align-items: flex-end; gap: 16px; height: 140px; padding-bottom: 44px; }
  .athlete-col { display: flex; flex-direction: column; align-items: center; gap: 8px; flex: 1; height: 100%; cursor: pointer; transition: var(--transition); }
  .athlete-col:hover .athlete-bar { filter: brightness(1.15); }
  .athlete-col:hover .athlete-avatar { transform: scale(1.1); }
  .athlete-bar-wrap { flex: 1; width: 100%; display: flex; align-items: flex-end; }
  .athlete-bar { width: 100%; border-radius: 6px 6px 0 0; min-height: 6px; transition: height 0.8s cubic-bezier(0.4,0,0.2,1), filter 0.2s; }
  .athlete-bar.green { background: var(--navy); }
  .athlete-bar.amber { background: var(--amber); }
  .athlete-bar.red { background: var(--red); }
  .athlete-avatar { width: 34px; height: 34px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; font-family: var(--head); transition: transform 0.2s ease; }
  .athlete-avatar.green { background: var(--bg2); color: var(--navy); border: 2px solid var(--navy); }
  .athlete-avatar.amber { background: #fffbeb; color: var(--amber); border: 2px solid var(--amber); }
  .athlete-avatar.red { background: var(--red-light); color: var(--red); border: 2px solid var(--red); }

  .clients-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px,1fr)); gap: 12px; }
  .client-card { background: var(--white); border: 1px solid var(--border); border-radius: 10px; padding: 18px; cursor: pointer; transition: var(--transition); box-shadow: var(--shadow-sm); position: relative; overflow: hidden; }
  .client-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; transition: height 0.2s ease; }
  .client-card.green::before { background: var(--navy); }
  .client-card.amber::before { background: var(--amber); }
  .client-card.red::before { background: var(--red); }
  .client-card:hover { border-color: var(--border2); transform: translateY(-3px); box-shadow: var(--shadow-lg); }
  .client-card:hover::before { height: 4px; }
  .client-card-top { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 12px; }
  .client-ava { width: 42px; height: 42px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 800; flex-shrink: 0; font-family: var(--head); }
  .client-ava.green { background: var(--bg2); color: var(--navy); }
  .client-ava.amber { background: #fffbeb; color: var(--amber); }
  .client-ava.red { background: var(--red-light); color: var(--red); }
  .client-info { flex: 1; min-width: 0; }
  .client-name { font-family: var(--head); font-size: 14px; font-weight: 700; color: var(--navy); }
  .client-sub { font-size: 11px; color: var(--muted2); margin-top: 2px; }
  .stake-badge {
    display: inline-flex; align-items: center; gap: 4px;
    margin-top: 6px; padding: 2px 8px; border-radius: 4px;
    background: var(--red-light); color: var(--red);
    font-family: var(--head); font-size: 10px; font-weight: 700;
    border: 1px solid rgba(217,45,32,0.2);
  }
  .score-ring { margin-left: auto; position: relative; width: 46px; height: 46px; flex-shrink: 0; }
  .score-ring svg { transform: rotate(-90deg); }
  .score-num { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; font-family: var(--head); }
  .client-stats { display: grid; grid-template-columns: repeat(3,1fr); gap: 6px; margin-bottom: 8px; }
  .stat-box { background: var(--bg); border-radius: 8px; padding: 8px; text-align: center; border: 1px solid var(--border); transition: var(--transition); }
  .stat-box-val { font-family: var(--head); font-size: 14px; font-weight: 700; color: var(--navy); }
  .stat-box-key { font-size: 9px; color: var(--muted2); text-transform: uppercase; letter-spacing: 0.05em; margin-top: 1px; font-weight: 600; }
  .client-compliance { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; margin-bottom: 10px; }
  .compliance-box { background: var(--bg); border-radius: 8px; padding: 8px 10px; border: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; }
  .compliance-label { font-size: 10px; color: var(--muted2); font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; }
  .compliance-val { font-family: var(--head); font-size: 15px; font-weight: 700; }
  .progress-track { height: 4px; background: var(--border); border-radius: 2px; overflow: hidden; margin-bottom: 4px; }
  .progress-fill { height: 100%; border-radius: 2px; transition: width 1s ease; }
  .progress-labels { display: flex; justify-content: space-between; font-size: 10px; color: var(--muted2); font-weight: 500; margin-bottom: 8px; }
  .card-badges { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 8px; }
  .badge { display: inline-flex; align-items: center; gap: 4px; font-size: 10px; font-weight: 600; padding: 3px 8px; border-radius: 4px; }
  .badge.momentum-up { background: var(--bg2); color: var(--navy); }
  .badge.momentum-down { background: var(--red-light); color: var(--red); }
  .badge.momentum-flat { background: var(--bg); color: var(--muted2); border: 1px solid var(--border); }
  .badge.dropout-high { background: var(--red-light); color: var(--red); border: 1px solid rgba(217,45,32,0.2); }
  .badge.dropout-med { background: #fffbeb; color: var(--amber); border: 1px solid rgba(217,119,6,0.2); }
  .badge.dropout-low { background: var(--green-soft); color: var(--green); border: 1px solid rgba(5,150,105,0.2); }

  /* ═══ ALERTS ═══ */
  .alerts-panel { padding: 20px; }
  .alerts-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
  .alerts-title { font-family: var(--head); font-size: 14px; font-weight: 700; color: var(--navy); display: flex; align-items: center; gap: 8px; }
  .alert-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--red); animation: pulse 2s infinite; }
  .alerts-count { font-size: 11px; color: var(--muted2); font-weight: 500; }
  .alert-card { background: var(--bg); border: 1px solid var(--border); border-radius: 10px; padding: 12px 14px; margin-bottom: 8px; cursor: pointer; transition: var(--transition); display: flex; align-items: flex-start; gap: 12px; border-left: 3px solid transparent; }
  .alert-card:hover { background: var(--white); box-shadow: var(--shadow-md); transform: translateX(2px); }
  .alert-card.red { border-left-color: var(--red); }
  .alert-card.amber { border-left-color: var(--amber); }
  .alert-card.green { border-left-color: var(--navy); }
  .alert-ava { width: 36px; height: 36px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 800; flex-shrink: 0; font-family: var(--head); }
  .alert-ava.red { background: var(--red-light); color: var(--red); }
  .alert-ava.amber { background: #fffbeb; color: var(--amber); }
  .alert-ava.green { background: var(--bg2); color: var(--navy); }
  .alert-name { font-size: 13px; font-weight: 600; color: var(--navy); }
  .alert-text { font-size: 11px; color: var(--muted2); margin-top: 1px; line-height: 1.3; }
  .alert-meta { font-size: 10px; color: var(--muted2); margin-top: 3px; }
  .alert-score { margin-left: auto; font-size: 16px; font-weight: 800; font-family: var(--head); flex-shrink: 0; padding-left: 8px; }
  .alert-score.red { color: var(--red); }
  .alert-score.amber { color: var(--amber); }
  .alert-score.green { color: var(--navy); }

  /* ═══ DETAIL PAGE ═══ */
  .back-btn { background: none; border: 1px solid var(--border); color: var(--muted2); padding: 6px 14px; border-radius: 6px; cursor: pointer; font-size: 12px; font-weight: 600; transition: var(--transition); margin-bottom: 0; display: inline-flex; align-items: center; gap: 6px; }
  .back-btn:hover { color: var(--navy); border-color: var(--navy); background: var(--bg2); }
  .detail-actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-bottom: 20px; }
  .wa-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 6px 12px; border-radius: 6px;
    background: var(--whatsapp); color: white;
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
    border: none; cursor: pointer; text-decoration: none;
    transition: var(--transition);
  }
  .wa-btn:hover { background: #1ea857; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(37,211,102,0.25); }
  .contract-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 6px 12px; border-radius: 6px;
    background: var(--white); color: var(--navy);
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
    border: 1px solid var(--border); cursor: pointer; transition: var(--transition);
  }
  .contract-btn:hover { background: var(--bg2); border-color: var(--navy); }

  .detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px; }
  .detail-card { background: var(--white); border: 1px solid var(--border); border-radius: 10px; padding: 18px 20px; box-shadow: var(--shadow-sm); transition: var(--transition); }
  .detail-card:hover { box-shadow: var(--shadow-md); }
  .detail-card.full { grid-column: 1 / -1; }
  .detail-card-title { font-family: var(--head); font-size: 12px; font-weight: 700; color: var(--navy); text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 14px; }

  .pact-row { padding: 12px 0; border-bottom: 1px solid var(--border); display: flex; align-items: center; gap: 12px; cursor: pointer; transition: var(--transition); }
  .pact-row:hover { background: var(--bg); margin: 0 -8px; padding: 12px 8px; border-radius: 6px; }
  .pact-row:last-child { border-bottom: none; }
  .pact-row-week { font-family: var(--head); font-size: 12px; font-weight: 700; color: var(--navy); }
  .pact-row-ref { font-size: 9px; color: var(--muted2); font-family: monospace; margin-top: 2px; letter-spacing: 0.02em; }
  .pact-row-progress { display: flex; align-items: center; gap: 6px; min-width: 80px; }
  .pact-row-bar { height: 4px; width: 40px; background: var(--border); border-radius: 2px; overflow: hidden; }
  .pact-row-fill { height: 100%; transition: width 0.6s ease; }
  .pact-status { font-size: 10px; font-weight: 700; padding: 3px 8px; border-radius: 4px; text-transform: uppercase; letter-spacing: 0.06em; min-width: 55px; text-align: center; }
  .pact-status.active { background: var(--bg2); color: var(--navy); }
  .pact-status.kept { background: var(--green-soft); color: var(--green); }
  .pact-status.partial { background: #fffbeb; color: var(--amber); }
  .pact-status.broken { background: var(--red-light); color: var(--red); }

  /* ═══ MODALS ═══ */
  .modal-overlay { position: fixed; inset: 0; background: rgba(10,37,64,0.55); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 16px; animation: fadeIn 0.2s ease; }
  .modal { background: var(--white); border-radius: 12px; width: 100%; max-width: 500px; max-height: 90vh; overflow: auto; box-shadow: var(--shadow-lg); animation: fadeSlideUp 0.3s ease; }
  .modal-header { padding: 20px 24px 16px; border-bottom: 1px solid var(--border); display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
  .modal-close { width: 28px; height: 28px; border: 1px solid var(--border); background: var(--bg); border-radius: 6px; cursor: pointer; font-size: 16px; color: var(--muted2); line-height: 1; padding: 0; }
  .modal-close:hover { color: var(--navy); border-color: var(--navy); }
  .modal-body { padding: 20px 24px; }
  .modal-footer { padding: 14px 24px; border-top: 1px solid var(--border); background: var(--bg); display: flex; gap: 8px; justify-content: flex-end; }
  .btn-primary { padding: 9px 18px; border-radius: 6px; border: none; background: var(--navy); color: white; cursor: pointer; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; transition: var(--transition); }
  .btn-primary:hover { background: #082039; transform: translateY(-1px); box-shadow: var(--shadow-md); }
  .btn-primary:disabled { opacity: 0.4; cursor: not-allowed; }
  .btn-secondary { padding: 9px 16px; border-radius: 6px; border: 1px solid var(--border); background: var(--white); color: var(--muted); cursor: pointer; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; }
  .btn-secondary:hover { color: var(--navy); border-color: var(--navy); }
  .btn-wa { background: var(--whatsapp); color: white; }
  .btn-wa:hover { background: #1ea857; }
  .form-field { display: flex; flex-direction: column; gap: 4px; margin-bottom: 14px; }
  .form-label { font-size: 9px; font-weight: 700; color: var(--muted2); text-transform: uppercase; letter-spacing: 0.1em; }
  .form-input { padding: 10px 12px; border-radius: 6px; border: 1px solid var(--border); background: var(--bg); font-size: 13px; color: var(--navy); outline: none; transition: var(--transition); }
  .form-input:focus { border-color: var(--navy); background: var(--white); }

  /* ═══ ATHLETE APP ═══ */
  .athlete-app {
    min-height: 100vh; background: var(--navy);
    display: flex; align-items: center; justify-content: center;
    padding: 16px;
  }
  .athlete-frame {
    width: 100%; max-width: 420px; height: 90vh; max-height: 900px;
    background: var(--bg); border-radius: 32px; overflow: hidden;
    box-shadow: 0 30px 80px rgba(0,0,0,0.5);
    border: 1px solid rgba(255,255,255,0.08);
    display: flex; flex-direction: column;
    position: relative;
  }
  .athlete-statusbar {
    padding: 10px 28px 4px; display: flex; justify-content: space-between;
    font-size: 12px; font-weight: 600; color: var(--navy);
  }
  .athlete-header {
    padding: 12px 20px; display: flex; align-items: center; justify-content: space-between;
    flex-shrink: 0; background: var(--bg);
  }
  .athlete-brand { display: flex; align-items: center; gap: 8px; }
  .athlete-brand-text { font-family: var(--head); font-weight: 800; font-size: 13px; color: var(--navy); letterSpacing: 0.02em; }
  .athlete-brand-text span { color: var(--red); }
  .athlete-tag {
    font-size: 8.5px; font-weight: 700; text-transform: uppercase;
    padding: 2px 6px; border-radius: 3px;
    background: rgba(217,45,32,0.1); color: var(--red);
    letter-spacing: 0.08em;
  }
  .athlete-header-actions { display: flex; gap: 6px; }
  .athlete-icon-btn {
    height: 32px; padding: 0 10px; border-radius: 6px;
    background: var(--white); border: 1px solid var(--border);
    color: var(--muted); cursor: pointer; display: flex; align-items: center; gap: 6px;
    font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em;
  }
  .athlete-icon-btn:hover { border-color: var(--navy); color: var(--navy); }
  .athlete-icon-btn.active { background: var(--navy); color: white; border-color: var(--navy); }
  .athlete-icon-btn.amber { background: var(--amber); color: white; border-color: var(--amber); }

  .athlete-scroll {
    flex: 1; overflow-y: auto; padding: 16px 20px 100px;
  }
  .athlete-scroll::-webkit-scrollbar { display: none; }

  .a-eyebrow { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
  .a-eyebrow .bar { width: 4px; height: 14px; border-radius: 2px; background: var(--red); }
  .a-eyebrow .text { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.2em; color: var(--red); }
  .a-headline { font-family: var(--head); font-weight: 800; font-size: 32px; color: var(--navy); letter-spacing: -0.025em; line-height: 0.95; }
  .a-headline span { color: var(--red); }
  .a-sub { margin-top: 10px; font-size: 13px; color: var(--muted); line-height: 1.5; }

  .a-card { background: var(--white); border: 1px solid var(--border); border-radius: 10px; padding: 14px; box-shadow: var(--shadow-sm); }
  .a-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .a-stat-tile { background: var(--white); border: 1px solid var(--border); border-radius: 10px; padding: 12px; box-shadow: var(--shadow-sm); }
  .a-stat-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.18em; color: var(--muted2); }
  .a-stat-value { font-family: var(--head); font-weight: 800; font-size: 24px; letter-spacing: -0.02em; line-height: 1; margin-top: 4px; color: var(--navy); }
  .a-stat-sub { font-size: 10px; margin-top: 4px; font-weight: 500; color: var(--muted); }

  .a-pax-card {
    background: var(--white); border: 1px solid var(--border); border-radius: 10px;
    overflow: hidden; box-shadow: var(--shadow-md);
  }
  .a-pax-card-header {
    background: var(--navy); color: white; padding: 8px 12px;
    display: flex; align-items: center; gap: 8px;
  }
  .a-pax-card-header .label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.2em; }
  .a-pax-avatar {
    width: 22px; height: 22px; border-radius: 4px; background: white;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }
  .a-pax-avatar span { font-family: var(--head); font-weight: 900; font-size: 11px; color: var(--navy); line-height: 1; }
  .a-pax-card-body { padding: 14px; font-size: 13px; line-height: 1.55; color: var(--muted); }

  .a-exercise-list { background: var(--white); border: 1px solid var(--border); border-radius: 10px; overflow: hidden; box-shadow: var(--shadow-sm); }
  .a-exercise-row { display: flex; align-items: center; gap: 10px; padding: 10px 14px; border-top: 1px solid var(--border); }
  .a-exercise-row:first-child { border-top: none; }
  .a-exercise-marker { width: 4px; height: 24px; border-radius: 2px; }
  .a-exercise-marker.main { background: var(--red); }
  .a-exercise-marker.warmup, .a-exercise-marker.cool { background: var(--border); }
  .a-exercise-marker.super { background: var(--navy); }
  .a-exercise-name { font-size: 12.5px; font-weight: 600; color: var(--navy); }
  .a-exercise-detail { font-size: 10.5px; color: var(--muted2); margin-top: 1px; }

  .a-cta {
    width: 100%; height: 52px; border-radius: 8px; border: none; cursor: pointer;
    background: var(--red); color: white;
    font-size: 12px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.12em;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    box-shadow: 0 4px 14px rgba(217,45,32,0.25);
    transition: var(--transition);
  }
  .a-cta:hover { background: #b82419; transform: translateY(-1px); }
  .a-cta:disabled { opacity: 0.4; cursor: not-allowed; }
  .a-cta.green { background: var(--green); box-shadow: 0 4px 14px rgba(5,150,105,0.25); }
  .a-cta-sub { text-align: center; font-size: 10px; font-weight: 600; color: var(--muted2); text-transform: uppercase; letter-spacing: 0.08em; margin-top: 8px; }

  .a-tabbar {
    position: absolute; bottom: 0; left: 0; right: 0;
    background: white; border-top: 1px solid var(--border);
    display: grid; grid-template-columns: repeat(4, 1fr);
    padding-bottom: 16px;
  }
  .a-tab {
    display: flex; flex-direction: column; align-items: center; gap: 2px;
    padding: 10px 0; cursor: pointer; transition: color 0.2s;
    color: var(--muted2);
    background: none; border: none;
  }
  .a-tab.active { color: var(--red); }
  .a-tab-icon { font-size: 16px; line-height: 1; }
  .a-tab-label { font-size: 9.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; }
  .a-tab-active-bar { width: 24px; height: 2px; border-radius: 1px; background: var(--red); margin-top: 1px; }

  /* ═══ SUNDAY CEREMONY ═══ */
  .ceremony-overlay {
    position: absolute; inset: 0; z-index: 100;
    background: var(--navy); display: flex; flex-direction: column;
    animation: fadeIn 0.3s ease;
  }
  .ceremony-header { padding: 16px 20px; display: flex; justify-content: space-between; align-items: center; }
  .ceremony-progress { padding: 0 20px; }
  .ceremony-progress-bar { display: flex; gap: 4px; }
  .ceremony-progress-step { flex: 1; height: 4px; border-radius: 2px; background: rgba(255,255,255,0.15); transition: background 0.3s; }
  .ceremony-progress-step.done { background: var(--red); }
  .ceremony-progress-text { font-size: 9.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.2em; color: rgba(255,255,255,0.5); margin-top: 8px; }
  .ceremony-body { flex: 1; padding: 16px 20px; overflow-y: auto; color: white; }
  .ceremony-body::-webkit-scrollbar { display: none; }
  .ceremony-headline { font-family: var(--head); font-weight: 800; font-size: 32px; line-height: 0.95; letter-spacing: -0.025em; }
  .ceremony-headline span { color: var(--red); }
  .ceremony-sub { margin-top: 16px; font-size: 13px; color: rgba(255,255,255,0.75); line-height: 1.6; }
  .ceremony-footer { padding: 16px 20px 24px; border-top: 1px solid rgba(255,255,255,0.08); }
  .ceremony-pact-card {
    background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px; padding: 16px; margin-top: 20px;
  }
  .ceremony-pact-row { display: flex; flex-direction: column; gap: 6px; padding: 10px 0; border-top: 1px solid rgba(255,255,255,0.08); }
  .ceremony-pact-row:first-child { padding-top: 0; border-top: none; }
  .ceremony-pact-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.2em; color: rgba(255,255,255,0.5); }
  .ceremony-pact-value { font-size: 14px; font-weight: 700; color: white; }
  .ceremony-stake-toggle {
    background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px; padding: 16px; margin-top: 16px; transition: var(--transition);
  }
  .ceremony-stake-toggle.on { background: var(--red); border-color: var(--red); }
  .toggle-switch { width: 40px; height: 22px; border-radius: 11px; background: rgba(255,255,255,0.15); position: relative; cursor: pointer; transition: background 0.2s; flex-shrink: 0; }
  .toggle-switch.on { background: white; }
  .toggle-knob { width: 18px; height: 18px; border-radius: 50%; background: var(--muted2); position: absolute; top: 2px; left: 2px; transition: all 0.2s; }
  .toggle-switch.on .toggle-knob { left: 20px; background: var(--red); }
  .signature-pad {
    background: var(--bg); border: 2px dashed var(--border2);
    border-radius: 8px; height: 80px; display: flex; align-items: center; justify-content: center;
    cursor: pointer; margin-top: 8px; transition: var(--transition);
  }
  .signature-pad.signed { background: rgba(217,45,32,0.05); border-color: var(--red); }
  .signature-text { font-family: 'Caveat', cursive; font-size: 26px; font-weight: 700; color: var(--red); }

  /* ═══ CONTRACT ═══ */
  .contract-doc { padding: 24px 28px 32px; }
  .contract-doc-header { text-align: center; padding-bottom: 16px; border-bottom: 1px solid var(--border); margin-bottom: 20px; }
  .contract-doc-brand { font-family: var(--head); font-weight: 800; font-size: 18px; color: var(--navy); }
  .contract-doc-brand span { color: var(--red); }
  .contract-doc-tagline { font-size: 10px; color: var(--muted2); text-transform: uppercase; letter-spacing: 0.16em; margin-top: 4px; }
  .contract-section { margin-bottom: 18px; }
  .contract-section-title { font-size: 10px; font-weight: 700; color: var(--navy); text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 8px; }
  .contract-panel { padding: 12px; background: var(--bg); border-radius: 6px; border: 1px solid var(--border); }
  .contract-sig-box { padding: 14px; background: var(--bg); border-radius: 6px; border: 1px solid var(--border); text-align: center; }
  .contract-sig-box.athlete { background: rgba(217,45,32,0.05); border: 2px solid var(--red); }
  .contract-sig { font-family: 'Caveat', cursive; font-size: 22px; font-weight: 700; line-height: 1.1; }

  /* ═══ Mobile responsive ═══ */
  @media (max-width: 900px) {
    .app { flex-direction: column; height: auto; min-height: 100vh; }
    .sidebar { width: 100%; flex-direction: row; align-items: center; padding: 0; }
    .sidebar-logo { padding: 12px 16px; border-bottom: none; border-right: 1px solid rgba(255,255,255,0.08); margin-bottom: 0; flex-shrink: 0; }
    .sidebar-logo .brand { font-size: 14px; }
    .sidebar-logo .tagline { display: none; }
    .nav-item { padding: 14px 12px; font-size: 12px; border-left: none; border-bottom: 3px solid transparent; }
    .nav-item.active { border-left-color: transparent; border-bottom-color: var(--red); }
    .sidebar-bottom { margin-top: 0; padding: 8px 12px; border-top: none; border-left: 1px solid rgba(255,255,255,0.08); margin-left: auto; display: flex; align-items: center; gap: 8px; }
    .sidebar-bottom > div { display: none; }
    .refresh-btn { margin-top: 0; padding: 8px 14px; width: auto; font-size: 11px; }
    .topbar { padding: 12px 16px; flex-direction: column; align-items: flex-start; gap: 10px; }
    .status-filters { width: 100%; overflow-x: auto; flex-wrap: nowrap; }
    .content { grid-template-columns: 1fr; }
    .summary-row { grid-template-columns: 1fr 1fr; gap: 8px; }
    .clients-grid { grid-template-columns: 1fr; }
    .detail-grid { grid-template-columns: 1fr; }
  }
`;

// ════════════════════════ HELPERS ════════════════════════
const initials = (name) => (name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

const ragColor = (score) => {
  if (score >= 80) return 'green';
  if (score >= 55) return 'amber';
  return 'red';
};
const ragLabel = (score) => {
  if (score >= 80) return 'PUSH FORWARD';
  if (score >= 55) return 'CAUTION';
  return 'HOLD BACK';
};

const calcRag = (health, moods, weekData) => {
  let score = 0;
  const avgSteps = weekData.length > 0 ? weekData.reduce((s,d)=>s+(d.steps||0),0)/weekData.length : 0;
  score += Math.min(25, (avgSteps/10000)*25);
  const avgCals = weekData.length > 0 ? weekData.reduce((s,d)=>s+(d.calories||0),0)/weekData.length : 0;
  score += avgCals > 1200 && avgCals < 2800 ? 25 : avgCals > 800 ? 12 : 0;
  const avgMood = moods.length > 0 ? moods.reduce((s,m)=>s+m.rating,0)/moods.length : 3;
  score += (avgMood/5)*15;
  if (health && health.steps > 0) score += 10;
  score += weekData.length >= 3 ? 25 : (weekData.length/5)*25;
  return Math.round(score);
};

const sessionAdherence = (weekSessions) => {
  if (!weekSessions || weekSessions.length === 0) return null;
  const planned = weekSessions.filter(s => !s.is_rest).length;
  const completed = weekSessions.filter(s => s.completed).length;
  if (planned === 0) return null;
  return Math.round((completed/planned)*100);
};

const nutritionCompliance = (nutritionDays, client) => {
  if (!nutritionDays || nutritionDays.length === 0) return null;
  const calTarget = client.calorie_target || 2100;
  const protTarget = client.protein_target || 160;
  const compliant = nutritionDays.filter(d => (d.calories||0)>=calTarget*0.8 && (d.protein||0)>=protTarget*0.8).length;
  return Math.round((compliant/nutritionDays.length)*100);
};

const calcMomentum = (weekData, prevWeekData) => {
  if (!weekData.length || !prevWeekData.length) return 'flat';
  const thisSteps = weekData.reduce((s,d)=>s+(d.steps||0),0)/weekData.length;
  const prevSteps = prevWeekData.reduce((s,d)=>s+(d.steps||0),0)/prevWeekData.length;
  const thisCals = weekData.reduce((s,d)=>s+(d.calories||0),0)/weekData.length;
  const prevCals = prevWeekData.reduce((s,d)=>s+(d.calories||0),0)/prevWeekData.length;
  const combined = (thisSteps-prevSteps)/1000 + (thisCals-prevCals)/200;
  if (combined > 0.5) return 'up';
  if (combined < -0.5) return 'down';
  return 'flat';
};

const calcDropoutRisk = (moods, weekData, weekSessions, conversations) => {
  let risk = 0;
  const avgMood = moods.length > 0 ? moods.reduce((s,m)=>s+m.rating,0)/moods.length : 3;
  if (avgMood < 2) risk += 30; else if (avgMood < 3) risk += 15;
  const avgSteps = weekData.length > 0 ? weekData.reduce((s,d)=>s+(d.steps||0),0)/weekData.length : 0;
  if (avgSteps < 3000) risk += 20; else if (avgSteps < 5000) risk += 10;
  const adherence = sessionAdherence(weekSessions);
  if (adherence !== null && adherence < 40) risk += 25; else if (adherence !== null && adherence < 60) risk += 10;
  if (weekData.length < 3) risk += 15;
  if (conversations !== null && conversations < 3) risk += 10;
  if (risk >= 50) return 'high';
  if (risk >= 25) return 'medium';
  return 'low';
};

const complianceColor = (val) => {
  if (val === null) return 'var(--muted2)';
  if (val >= 80) return 'var(--navy)';
  if (val >= 50) return 'var(--amber)';
  return 'var(--red)';
};

const waLink = (phone, message) => {
  const cleanPhone = (phone || '').replace(/[^\d]/g, '');
  const encoded = encodeURIComponent(message || '');
  return `https://wa.me/${cleanPhone}${encoded ? '?text=' + encoded : ''}`;
};

// ════════════════════════ ICONS ════════════════════════
const Icons = {
  WhatsApp: ({ size = 14, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={{ flexShrink: 0 }}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  ),
};

// ════════════════════════ ROOT ════════════════════════
export default function App() {
  const [user, setUser] = useState(null); // null | { role: 'coach' | 'athlete', id }

  if (!user) {
    return (
      <>
        <style>{styles}</style>
        <Login onLogin={setUser} />
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      {user.role === 'coach'
        ? <CoachDashboard onLogout={() => setUser(null)} />
        : <AthleteApp athleteId={user.id} onLogout={() => setUser(null)} />}
    </>
  );
}

// ════════════════════════ LOGIN ════════════════════════
function Login({ onLogin }) {
  return (
    <div className="login-screen">
      <div className="login-box fade-in">
        <div className="login-brand">
          <div className="login-brand-mark">P</div>
          <div className="login-brand-text">PACT<span>.</span>HEALTH</div>
        </div>

        <div className="login-eyebrow">
          <div className="bar" />
          <div className="text">Demo · Continue as</div>
        </div>
        <h1 className="login-headline">KEEP YOUR<br /><span>WORD.</span></h1>
        <p className="login-sub">
          The accountability layer that lives between you and your coach. Pick a profile to see how it works.
        </p>

        <div className="login-options">
          <div className="login-card" onClick={() => onLogin({ role: 'coach', id: COACH.id })}>
            <div className="login-card-icon coach">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.25">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <div className="login-card-content">
              <div className="login-card-role">Coach</div>
              <div className="login-card-name">Will Young</div>
              <div className="login-card-meta">{ATHLETES.length} active athletes</div>
            </div>
            <div className="login-card-arrow">→</div>
          </div>

          <div className="login-card" onClick={() => onLogin({ role: 'athlete', id: 'alex-001' })}>
            <div className="login-card-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.25">
                <circle cx="12" cy="8" r="5" /><path d="M3 21a9 9 0 0 1 18 0" />
              </svg>
            </div>
            <div className="login-card-content">
              <div className="login-card-role">Athlete</div>
              <div className="login-card-name">Alex Joyner</div>
              <div className="login-card-meta">Cutting · 1.4 kg from target</div>
            </div>
            <div className="login-card-arrow">→</div>
          </div>
        </div>

        <div className="login-footer">
          Demo mode · all data mocked · production will use Supabase auth
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// COACH DASHBOARD
// ════════════════════════════════════════════════════════════════════════
function CoachDashboard({ onLogout }) {
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [contractFor, setContractFor] = useState(null);

  // Score every athlete
  const scoredClients = ATHLETES.map(c => {
    const d = CLIENT_DATA[c.id];
    const score = calcRag(d.health, d.moods, d.weekData);
    return { ...c, score, color: ragColor(score) };
  });

  const filteredClients = scoredClients.filter(c => {
    if (filter === 'all') return true;
    if (filter === 'push') return c.color === 'green';
    if (filter === 'caution') return c.color === 'amber';
    if (filter === 'holdback') return c.color === 'red';
    return true;
  });

  const redClients = scoredClients.filter(c => c.color === 'red');
  const greenClients = scoredClients.filter(c => c.color === 'green');
  const amberClients = scoredClients.filter(c => c.color === 'amber');
  const avgScore = scoredClients.length > 0 ? Math.round(scoredClients.reduce((s,c)=>s+c.score, 0)/scoredClients.length) : 0;
  const highDropout = scoredClients.filter(c => {
    const d = CLIENT_DATA[c.id];
    return calcDropoutRisk(d.moods, d.weekData, d.weekSessions, d.conversations) === 'high';
  }).length;

  // Stakes summary
  const activeStakes = Object.values(STAKES);
  const totalStakeAmount = activeStakes.reduce((s, st) => s + st.amount, 0);

  const alertClients = [...redClients, ...amberClients].slice(0, 10);

  const selectedAthlete = selected || null;
  const selectedData = selectedAthlete ? CLIENT_DATA[selectedAthlete.id] : null;

  return (
    <div className="app">
      {/* ── SIDEBAR ── */}
      <div className="sidebar">
        <div className="sidebar-logo">
          <div className="brand">PACT<span>.</span>HEALTH</div>
          <div className="tagline">PT Intelligence</div>
        </div>
        <div className="nav-item active">◆ Athletes</div>
        <div className="nav-item">↗ Reports</div>
        <div className="nav-item" style={{ cursor: 'pointer', marginTop: 8 }} onClick={() => setShowInvite(true)}>
          + Invite athlete
        </div>
        <div className="sidebar-bottom">
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginBottom: 6 }}>Signed in as</div>
          <div style={{ fontSize: 12, color: 'white', fontWeight: 600, marginBottom: 2 }}>{COACH.name}</div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{COACH.level}</div>
          <div className="sidebar-logout" onClick={onLogout} style={{ marginTop: 12 }}>↪ Sign out</div>
        </div>
      </div>

      {/* ── MAIN ── */}
      <div className="main">
        <div className="topbar">
          <div>
            <div className="topbar-title">{selected ? selected.name : 'All clients'}</div>
            <div className="topbar-sub">
              {selected
                ? ragLabel(selected.score || 0)
                : `${ATHLETES.length} athletes · ${redClients.length} need attention · ${highDropout} dropout risk`}
            </div>
          </div>
          {!selected && (
            <div className="status-filters">
              {[
                { key: 'all', label: 'All' },
                { key: 'push', label: 'Push Forward' },
                { key: 'caution', label: 'Caution' },
                { key: 'holdback', label: 'Hold Back' },
              ].map(f => (
                <div key={f.key} className={`status-pill ${f.key} ${filter === f.key ? 'active' : ''}`} onClick={() => setFilter(f.key)}>
                  {f.label}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="content">
          <div className="content-main">
            {selected && selectedData ? (
              <ClientDetail
                client={selected}
                data={selectedData}
                onBack={() => setSelected(null)}
                onViewContract={(pact) => setContractFor({
                  pact,
                  client: selected,
                  stake: STAKES[selected.id],
                  cosigners: COSIGNERS[selected.id] || [],
                })}
              />
            ) : (
              <DashboardOverview
                avgScore={avgScore}
                redClients={redClients}
                greenClients={greenClients}
                highDropout={highDropout}
                totalStakeAmount={totalStakeAmount}
                activeStakeCount={activeStakes.length}
                scoredClients={scoredClients}
                filteredClients={filteredClients}
                onSelect={setSelected}
              />
            )}
          </div>

          <div className="content-side">
            {selected && selectedData ? (
              <DetailSidePanel client={selected} data={selectedData} />
            ) : (
              <AlertsPanel alertClients={alertClients} greenClients={greenClients} onSelect={setSelected} />
            )}
          </div>
        </div>
      </div>

      {showInvite && <InviteModal onClose={() => setShowInvite(false)} />}
      {contractFor && (
        <ContractModal
          pact={contractFor.pact}
          client={contractFor.client}
          stake={contractFor.stake}
          cosigners={contractFor.cosigners}
          onClose={() => setContractFor(null)}
        />
      )}
    </div>
  );
}

// ── Dashboard Overview ────────────────────────────────────────────────
function DashboardOverview({ avgScore, redClients, greenClients, highDropout, totalStakeAmount, activeStakeCount, scoredClients, filteredClients, onSelect }) {
  return (
    <>
      <div className="summary-row">
        {[
          { label: 'Avg readiness', val: avgScore,             color: 'navy',
            valColor: ragColor(avgScore) === 'green' ? 'var(--navy)' : ragColor(avgScore) === 'amber' ? 'var(--amber)' : 'var(--red)' },
          { label: 'Need attention', val: redClients.length,    color: 'red',
            valColor: redClients.length > 0 ? 'var(--red)' : 'var(--navy)' },
          { label: 'Dropout risk', val: highDropout,            color: 'amber',
            valColor: highDropout > 0 ? 'var(--red)' : 'var(--navy)' },
          { label: 'On track', val: greenClients.length,        color: 'green',
            valColor: 'var(--navy)' },
          { label: 'Active stakes', val: `£${totalStakeAmount}`, color: 'red',
            valColor: 'var(--red)', sub: `${activeStakeCount} athletes at risk` },
        ].map((item, idx) => (
          <div key={idx} className={`summary-card ${item.color} animate-in stagger-${idx+1}`}>
            <div className="summary-label">{item.label}</div>
            <div className="summary-val" style={{ color: item.valColor }}>{item.val}</div>
            {item.sub && <div className="summary-sub">{item.sub}</div>}
          </div>
        ))}
      </div>

      <div className="chart-section animate-in stagger-2">
        <div className="section-label">
          Readiness by athlete
          <span style={{ fontSize: 11, color: 'var(--muted2)', fontWeight: 400 }}>click to open profile</span>
        </div>
        <div className="athlete-chart">
          {scoredClients.map(c => (
            <div key={c.id} className="athlete-col" onClick={() => onSelect(c)}>
              <div className="athlete-bar-wrap">
                <div className={`athlete-bar ${c.color}`} style={{ height: `${Math.max(6, (c.score/100)*96)}px` }} />
              </div>
              <div className={`athlete-avatar ${c.color}`}>{initials(c.name)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="section-label animate-in stagger-3" style={{ marginBottom: 12 }}>Athletes</div>
      <div className="clients-grid">
        {filteredClients.map((c, idx) => (
          <ClientCard key={c.id} client={c} animDelay={Math.min(idx + 3, 6)} onClick={() => onSelect(c)} />
        ))}
      </div>
    </>
  );
}

// ── Client Card ───────────────────────────────────────────────────────
function ClientCard({ client, animDelay, onClick }) {
  const d = CLIENT_DATA[client.id];
  const stake = STAKES[client.id];
  const color = client.color;
  const score = client.score;

  const wl = client.start_weight && client.current_weight ? Math.abs(client.start_weight - client.current_weight).toFixed(1) : '—';
  const toGo = client.current_weight && client.target_weight ? Math.abs(client.current_weight - client.target_weight).toFixed(1) : '—';
  const isGaining = client.goal === 'muscle_building';
  const avgSteps = d.weekData.length > 0 ? Math.round(d.weekData.reduce((s,x)=>s+(x.steps||0), 0)/d.weekData.length) : 0;
  const avgProtein = d.weekData.length > 0 ? Math.round(d.weekData.reduce((s,x)=>s+(x.protein||0), 0)/d.weekData.length) : 0;
  const avgMood = d.moods.length > 0 ? (d.moods.reduce((s,m)=>s+m.rating, 0)/d.moods.length).toFixed(1) : '—';
  const adherence = sessionAdherence(d.weekSessions);
  const compliance = nutritionCompliance(d.nutritionDays, client);
  const momentum = calcMomentum(d.weekData, d.prevWeekData);
  const dropout = calcDropoutRisk(d.moods, d.weekData, d.weekSessions, d.conversations);
  const progressPct = client.start_weight && client.target_weight && client.current_weight
    ? Math.min(100, Math.abs((client.start_weight - client.current_weight) / (client.start_weight - client.target_weight)) * 100)
    : 0;

  const r = 18; const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  const ringColor = color === 'green' ? 'var(--navy)' : color === 'amber' ? 'var(--amber)' : 'var(--red)';

  return (
    <div className={`client-card ${color} animate-in stagger-${animDelay}`} onClick={onClick}>
      <div className="client-card-top">
        <div className={`client-ava ${color}`}>{initials(client.name)}</div>
        <div className="client-info">
          <div className="client-name">{client.name}</div>
          <div className="client-sub">{client.goal?.replace(/_/g, ' ')} · {client.gym}</div>
          {stake && (
            <div className="stake-badge">
              £{stake.amount} · {stake.breaks_so_far}/{stake.trigger_threshold}
            </div>
          )}
        </div>
        <div className="score-ring">
          <svg width="44" height="44" viewBox="0 0 44 44">
            <circle cx="22" cy="22" r={r} fill="none" stroke="var(--border)" strokeWidth="3" />
            <circle cx="22" cy="22" r={r} fill="none" stroke={ringColor} strokeWidth="3"
              strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" />
          </svg>
          <div className="score-num" style={{ color: ringColor }}>{score}</div>
        </div>
      </div>

      <div className="client-stats">
        <div className="stat-box">
          <div className="stat-box-val">{(avgSteps/1000).toFixed(1)}k</div>
          <div className="stat-box-key">Steps</div>
        </div>
        <div className="stat-box">
          <div className="stat-box-val">{avgProtein}g</div>
          <div className="stat-box-key">Protein</div>
        </div>
        <div className="stat-box">
          <div className="stat-box-val">{avgMood}</div>
          <div className="stat-box-key">Mood</div>
        </div>
      </div>

      <div className="client-compliance">
        <div className="compliance-box">
          <div>
            <div className="compliance-label">Sessions</div>
            <div className="compliance-val" style={{ color: complianceColor(adherence) }}>
              {adherence !== null ? `${adherence}%` : '—'}
            </div>
          </div>
          <div style={{ fontSize: 16, color: complianceColor(adherence) }}>
            {adherence === null ? '–' : adherence >= 80 ? '✓' : adherence >= 50 ? '~' : '✗'}
          </div>
        </div>
        <div className="compliance-box">
          <div>
            <div className="compliance-label">Nutrition</div>
            <div className="compliance-val" style={{ color: complianceColor(compliance) }}>
              {compliance !== null ? `${compliance}%` : '—'}
            </div>
          </div>
          <div style={{ fontSize: 16, color: complianceColor(compliance) }}>
            {compliance === null ? '–' : compliance >= 80 ? '✓' : compliance >= 50 ? '~' : '✗'}
          </div>
        </div>
      </div>

      <div className="card-badges">
        <span className={`badge momentum-${momentum}`}>
          {momentum === 'up' ? '↑ Building' : momentum === 'down' ? '↓ Declining' : '→ Steady'}
        </span>
        <span className={`badge dropout-${dropout}`}>
          {dropout === 'high' ? '⚠ High risk' : dropout === 'medium' ? '~ Monitor' : '✓ Low risk'}
        </span>
      </div>

      <div className="progress-track">
        <div className="progress-fill" style={{
          width: `${progressPct}%`,
          background: color === 'green' ? 'var(--navy)' : color === 'amber' ? 'var(--amber)' : 'var(--red)'
        }} />
      </div>
      <div className="progress-labels">
        <span>{wl}kg {isGaining ? 'gained' : 'lost'}</span>
        <span>{toGo}kg to go</span>
      </div>
    </div>
  );
}


// ── Client Detail (the deep-dive view) ────────────────────────────────
function ClientDetail({ client, data, onBack, onViewContract }) {
  const stake = STAKES[client.id];
  const cosigners = COSIGNERS[client.id] || [];
  const allPacts = PACTS[client.id] || [];
  const activePact = allPacts.find(p => p.status === 'active') || allPacts[allPacts.length - 1];

  const wl = client.start_weight && client.current_weight ? Math.abs(client.start_weight - client.current_weight).toFixed(1) : '—';
  const toGo = client.current_weight && client.target_weight ? Math.abs(client.current_weight - client.target_weight).toFixed(1) : '—';
  const isGaining = client.goal === 'muscle_building';
  const avgSteps = data.weekData.length > 0 ? Math.round(data.weekData.reduce((s,d)=>s+(d.steps||0), 0)/data.weekData.length) : 0;
  const avgCals = data.weekData.length > 0 ? Math.round(data.weekData.reduce((s,d)=>s+(d.calories||0), 0)/data.weekData.length) : 0;
  const avgProtein = data.weekData.length > 0 ? Math.round(data.weekData.reduce((s,d)=>s+(d.protein||0), 0)/data.weekData.length) : 0;
  const adherence = sessionAdherence(data.weekSessions);
  const compliance = nutritionCompliance(data.nutritionDays, client);

  return (
    <div className="page-enter">
      <div className="detail-actions">
        <button className="back-btn" onClick={onBack}>← Back to all clients</button>

        {client.whatsapp_phone && (
          <a className="wa-btn"
             href={waLink(client.whatsapp_phone, `Hey ${client.name.split(' ')[0]}, quick check-in from Will.`)}
             target="_blank" rel="noopener noreferrer">
            <Icons.WhatsApp size={12} color="white" />
            Message
          </a>
        )}

        {activePact && (
          <button className="contract-btn" onClick={() => onViewContract(activePact)}>
            📜 View contract
          </button>
        )}

        {stake && (
          <span className="stake-badge" style={{ marginLeft: 'auto' }}>
            £{stake.amount} stake · {stake.breaks_so_far}/{stake.trigger_threshold} broken
          </span>
        )}
      </div>

      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontFamily: 'var(--head)', fontSize: 22, fontWeight: 800, color: 'var(--navy)', letterSpacing: '-0.015em' }}>
          {client.name}
        </h2>
        <div style={{ fontSize: 12, color: 'var(--muted2)', marginTop: 4 }}>
          {client.current_weight}kg · {wl}kg {isGaining ? 'gained' : 'lost'} · {toGo}kg to go ·
          <span style={{ fontWeight: 600, color: 'var(--navy)', marginLeft: 4 }}>{ragLabel(client.score || 0)}</span>
        </div>
      </div>

      <div className="detail-grid">
        <div className="detail-card">
          <div className="detail-card-title">Weekly nutrition averages</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
            <NutritionTile val={avgCals} label="Calories" target={client.calorie_target} />
            <NutritionTile val={`${avgProtein}g`} label="Protein" target={`${client.protein_target}g`} />
            <NutritionTile val={`${avgSteps.toLocaleString()}`} label="Steps" target={client.step_target?.toLocaleString()} />
            <NutritionTile val={`${compliance}%`} label="Compliance" target="80% goal" />
          </div>
        </div>

        <div className="detail-card">
          <div className="detail-card-title">Session adherence</div>
          <div style={{ fontFamily: 'var(--head)', fontSize: 32, fontWeight: 800, color: complianceColor(adherence), letterSpacing: '-0.02em' }}>
            {adherence !== null ? `${adherence}%` : '—'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted2)', marginTop: 4 }}>
            {data.weekSessions.filter(s => s.completed).length} of {data.weekSessions.length} this week
          </div>
        </div>

        <div className="detail-card full">
          <div className="detail-card-title">Weekly pacts</div>
          {allPacts.length === 0 ? (
            <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--muted2)', fontSize: 12 }}>
              No pacts signed yet
            </div>
          ) : (
            allPacts.slice().reverse().slice(0, 6).map(pact => {
              const weekDate = new Date(pact.week_start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
              const pct = pact.pacts_committed > 0 ? (pact.pacts_kept / pact.pacts_committed) * 100 : 0;
              return (
                <div key={pact.id} className="pact-row" onClick={() => onViewContract(pact)}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="pact-row-week">Week of {weekDate}</div>
                    <div className="pact-row-ref">{pact.reference}</div>
                  </div>
                  <div className="pact-row-progress">
                    <div className="pact-row-bar">
                      <div className="pact-row-fill" style={{
                        width: `${pct}%`,
                        background: pact.status === 'kept' ? 'var(--green)' :
                          pact.status === 'broken' ? 'var(--red)' :
                            pact.status === 'partial' ? 'var(--amber)' : 'var(--navy)'
                      }} />
                    </div>
                    <span style={{ fontSize: 10, color: 'var(--muted2)', fontFamily: 'var(--head)', fontWeight: 700 }}>
                      {pact.pacts_kept}/{pact.pacts_committed}
                    </span>
                  </div>
                  <span className={`pact-status ${pact.status}`}>{pact.status}</span>
                </div>
              );
            })
          )}
        </div>

        {cosigners.length > 0 && (
          <div className="detail-card">
            <div className="detail-card-title">Co-signers · who's watching</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {cosigners.map(c => (
                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
                  <div style={{ width: 30, height: 30, borderRadius: 6, background: 'var(--bg2)', color: 'var(--navy)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--head)', fontWeight: 700, fontSize: 11 }}>
                    {initials(c.name)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)' }}>{c.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--muted2)', textTransform: 'capitalize' }}>{c.relationship}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="detail-card">
          <div className="detail-card-title">Body weight tracking</div>
          <WeightSparkline weighIns={data.weighIns} client={client} />
        </div>
      </div>
    </div>
  );
}

function NutritionTile({ val, label, target }) {
  return (
    <div style={{ textAlign: 'center', padding: 8 }}>
      <div style={{ fontFamily: 'var(--head)', fontSize: 20, fontWeight: 700, color: 'var(--navy)' }}>{val}</div>
      <div style={{ fontSize: 9, color: 'var(--muted2)', textTransform: 'uppercase', letterSpacing: 0.05, fontWeight: 600, marginTop: 2 }}>{label}</div>
      {target && <div style={{ fontSize: 10, color: 'var(--muted2)', marginTop: 1 }}>/ {target}</div>}
    </div>
  );
}

function WeightSparkline({ weighIns, client }) {
  if (!weighIns || weighIns.length < 2) {
    return <div style={{ color: 'var(--muted2)', fontSize: 12 }}>Not enough weigh-in data yet</div>;
  }
  const sorted = [...weighIns].sort((a, b) => new Date(a.date) - new Date(b.date));
  const weights = sorted.map(w => w.weight);
  const minW = Math.floor(Math.min(...weights)) - 1;
  const maxW = Math.ceil(Math.max(...weights)) + 1;
  const range = maxW - minW || 1;
  const W = 500, H = 100, PADL = 30, PADR = 30, PADT = 14, PADB = 18;
  const innerW = W - PADL - PADR, innerH = H - PADT - PADB;

  const points = sorted.map((w, i) => ({
    x: PADL + (i / (sorted.length - 1)) * innerW,
    y: PADT + innerH - ((w.weight - minW) / range) * innerH,
  }));
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const totalChange = (weights[weights.length - 1] - weights[0]).toFixed(1);
  const isGaining = client.goal === 'muscle_building';
  const positive = parseFloat(totalChange) > 0;
  const changeColor = isGaining ? (positive ? 'var(--green)' : 'var(--red)') : (positive ? 'var(--red)' : 'var(--green)');

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 9, color: 'var(--muted2)', fontWeight: 600, textTransform: 'uppercase' }}>Current</div>
          <div style={{ fontFamily: 'var(--head)', fontSize: 16, fontWeight: 800 }}>{weights[weights.length - 1]}kg</div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 9, color: 'var(--muted2)', fontWeight: 600, textTransform: 'uppercase' }}>Target</div>
          <div style={{ fontFamily: 'var(--head)', fontSize: 16, fontWeight: 800 }}>{client.target_weight}kg</div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 9, color: 'var(--muted2)', fontWeight: 600, textTransform: 'uppercase' }}>Change</div>
          <div style={{ fontFamily: 'var(--head)', fontSize: 16, fontWeight: 800, color: changeColor }}>
            {positive ? '+' : ''}{totalChange}kg
          </div>
        </div>
      </div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
        <path d={path} fill="none" stroke="var(--navy)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="3" fill={i === points.length - 1 ? 'var(--red)' : 'var(--navy)'} />
        ))}
      </svg>
    </div>
  );
}

// ── Side panel (alerts / insights) ────────────────────────────────────
function AlertsPanel({ alertClients, greenClients, onSelect }) {
  return (
    <div className="alerts-panel">
      <div className="alerts-header">
        <div className="alerts-title">
          {alertClients.length > 0 && <div className="alert-dot" />}
          Alerts
        </div>
        <div className="alerts-count">{ATHLETES.length} athletes tracked</div>
      </div>
      {alertClients.length === 0 ? (
        <div style={{ color: 'var(--muted2)', fontSize: 12, textAlign: 'center', padding: '40px 0' }}>
          All athletes on track
        </div>
      ) : (
        alertClients.map((c, idx) => {
          const d = CLIENT_DATA[c.id];
          const avgProtein = d?.weekData?.length > 0 ? Math.round(d.weekData.reduce((s,x)=>s+(x.protein||0), 0)/d.weekData.length) : 0;
          const avgSteps = d?.weekData?.length > 0 ? Math.round(d.weekData.reduce((s,x)=>s+(x.steps||0), 0)/d.weekData.length) : 0;
          const avgMood = d?.moods?.length > 0 ? (d.moods.reduce((s,m)=>s+m.rating, 0)/d.moods.length).toFixed(1) : null;
          const dropout = d ? calcDropoutRisk(d.moods, d.weekData, d.weekSessions, d.conversations) : 'low';
          const momentum = d ? calcMomentum(d.weekData, d.prevWeekData) : 'flat';
          let alertText = '';
          if (c.color === 'red') {
            if (avgSteps < 3000) alertText = `Only ${(avgSteps/1000).toFixed(1)}k avg steps`;
            else if (avgProtein < 80) alertText = `Protein averaging ${avgProtein}g`;
            else if (avgMood && parseFloat(avgMood) <= 2) alertText = `Mood ${avgMood}/5 this week`;
            else alertText = 'Multiple metrics flagged';
          } else {
            alertText = avgProtein < 100 ? `Protein below target (${avgProtein}g)` : 'Worth monitoring this week';
          }
          return (
            <div key={c.id} className={`alert-card ${c.color} animate-in stagger-${Math.min(idx + 1, 6)}`} onClick={() => onSelect(c)}>
              <div className={`alert-ava ${c.color}`}>{initials(c.name)}</div>
              <div style={{ flex: 1 }}>
                <div className="alert-name">{c.name}</div>
                <div className="alert-text">{alertText}</div>
                <div className="alert-meta">
                  {momentum === 'down' && <span style={{ color: 'var(--red)', marginRight: 6 }}>down momentum</span>}
                  {dropout === 'high' && <span style={{ color: 'var(--red)', marginRight: 6 }}>dropout risk</span>}
                </div>
              </div>
              <div className={`alert-score ${c.color}`}>{c.score}</div>
            </div>
          );
        })
      )}
      {greenClients.length > 0 && (
        <>
          <div style={{ margin: '16px 0 10px', fontSize: 11, color: 'var(--muted2)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.06 }}>On track</div>
          {greenClients.map((c, idx) => (
            <div key={c.id} className="alert-card green animate-in" onClick={() => onSelect(c)} style={{ animationDelay: `${0.1 * idx}s` }}>
              <div className="alert-ava green">{initials(c.name)}</div>
              <div style={{ flex: 1 }}>
                <div className="alert-name">{c.name}</div>
                <div className="alert-text">All metrics green</div>
              </div>
              <div className="alert-score green">{c.score}</div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function DetailSidePanel({ client, data }) {
  const insights = generateInsights(client, data);
  return (
    <div className="alerts-panel">
      <div className="alerts-header">
        <div className="alerts-title">Pattern insights</div>
      </div>
      {insights.map((ins, idx) => (
        <div key={idx} className="animate-in" style={{
          padding: '12px 14px',
          borderRadius: 10,
          marginBottom: 8,
          background: ins.type === 'critical' ? 'var(--red-light)' : ins.type === 'warning' ? '#fffbeb' : 'var(--green-soft)',
          borderLeft: `3px solid ${ins.type === 'critical' ? 'var(--red)' : ins.type === 'warning' ? 'var(--amber)' : 'var(--green)'}`,
          fontSize: 12, lineHeight: 1.5, color: 'var(--muted)',
          animationDelay: `${idx * 0.1}s`,
        }}>
          <div style={{
            fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.06, marginBottom: 4,
            color: ins.type === 'critical' ? 'var(--red)' : ins.type === 'warning' ? 'var(--amber)' : 'var(--green)',
          }}>
            {ins.type}
          </div>
          {ins.text}
        </div>
      ))}
    </div>
  );
}

function generateInsights(client, data) {
  const insights = [];
  const avgProtein = data.weekData.length > 0 ? data.weekData.reduce((s,d)=>s+(d.protein||0), 0)/data.weekData.length : 0;
  const avgSteps = data.weekData.length > 0 ? data.weekData.reduce((s,d)=>s+(d.steps||0), 0)/data.weekData.length : 0;
  const avgMood = data.moods.length > 0 ? data.moods.reduce((s,m)=>s+m.rating, 0)/data.moods.length : null;
  if (avgProtein < (client.protein_target || 160) * 0.7) insights.push({ type: 'warning', text: `Protein averaging ${Math.round(avgProtein)}g vs ${client.protein_target}g target — under-fuelling recovery.` });
  if (avgMood !== null && avgMood < 2.5) insights.push({ type: 'critical', text: `Average mood ${avgMood.toFixed(1)}/5 this week — emotional state is a dropout risk. Consider a personal check-in.` });
  if (avgSteps < 4000) insights.push({ type: 'warning', text: `Daily steps averaging ${Math.round(avgSteps).toLocaleString()} — NEAT activity very low.` });
  if (avgSteps > 8000 && avgProtein > 130) insights.push({ type: 'positive', text: `Strong week — both NEAT and protein targets being met.` });
  const adherence = sessionAdherence(data.weekSessions);
  if (adherence !== null && adherence < 50) insights.push({ type: 'critical', text: `Session adherence at ${adherence}% — missed sessions compound.` });
  if (insights.length === 0) insights.push({ type: 'positive', text: 'Steady progress — keep the rhythm going.' });
  return insights.slice(0, 4);
}


// ════════════════════════════════════════════════════════════════════════
// SHARED MODALS
// ════════════════════════════════════════════════════════════════════════
function InviteModal({ onClose }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [step, setStep] = useState('form');

  const message = `Hey ${name || '[name]'}, Will here.

I've set you up with PAX — your AI training assistant, working alongside me. He'll check in with you between our sessions, help you stay on track with your nutrition and movement, and flag anything you need from me.

Quick 2-minute onboarding chat with him to get going?`;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <Icons.WhatsApp size={12} color="var(--whatsapp)" />
              <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--whatsapp)', textTransform: 'uppercase', letterSpacing: 0.12 }}>
                WhatsApp invite
              </span>
            </div>
            <h3 style={{ fontFamily: 'var(--head)', fontWeight: 800, fontSize: 20, color: 'var(--navy)', letterSpacing: '-0.01em', lineHeight: 1.2 }}>
              {step === 'sent' ? 'Sent ✓' : step === 'preview' ? 'Review the message' : 'Invite a client'}
            </h3>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {step === 'form' && (
            <>
              <div className="form-field">
                <label className="form-label">Client name</label>
                <input className="form-input" value={name} onChange={e => setName(e.target.value)} placeholder="Sarah Mitchell" autoFocus />
              </div>
              <div className="form-field">
                <label className="form-label">WhatsApp number</label>
                <input className="form-input" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+44 7700 900142" />
              </div>
              <div style={{ padding: 12, background: 'var(--bg)', borderRadius: 6, fontSize: 11, color: 'var(--muted2)', lineHeight: 1.5 }}>
                <strong style={{ color: 'var(--navy)' }}>What happens:</strong> WhatsApp opens with a pre-filled message. You hit send. PAX takes over the chat.
              </div>
            </>
          )}

          {step === 'preview' && (
            <>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted2)', textTransform: 'uppercase', letterSpacing: 0.08, marginBottom: 8 }}>
                What {name} will see
              </div>
              <div style={{ background: '#E5DDD5', borderRadius: 8, padding: 12 }}>
                <div style={{
                  background: 'white', padding: '10px 12px', borderRadius: 6,
                  fontSize: 12, lineHeight: 1.6, color: 'var(--muted)', whiteSpace: 'pre-wrap',
                  borderTopLeftRadius: 2,
                }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--whatsapp-dark)', marginBottom: 6 }}>
                    Will · via PACT
                  </div>
                  {message}
                </div>
              </div>
            </>
          )}

          {step === 'sent' && (
            <div style={{ padding: '20px 0', textAlign: 'center' }}>
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                background: 'rgba(37,211,102,0.15)', margin: '0 auto 16px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 28, color: 'var(--whatsapp)', fontWeight: 700,
              }}>✓</div>
              <div style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.6 }}>
                <strong style={{ color: 'var(--navy)' }}>{name}</strong> has the invite in their WhatsApp.
                <br />Add them to your roster after they reply.
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {step === 'form' && (
            <>
              <button className="btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn-primary" disabled={!name || !phone} onClick={() => setStep('preview')}>Preview message →</button>
            </>
          )}
          {step === 'preview' && (
            <>
              <button className="btn-secondary" onClick={() => setStep('form')}>← Edit</button>
              <a className="btn-primary btn-wa" href={waLink(phone, message)} target="_blank" rel="noopener noreferrer"
                 onClick={() => setTimeout(() => setStep('sent'), 200)}
                 style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}>
                <Icons.WhatsApp size={12} color="white" />
                Open WhatsApp
              </a>
            </>
          )}
          {step === 'sent' && <button className="btn-primary" onClick={onClose}>Done</button>}
        </div>
      </div>
    </div>
  );
}

function ContractModal({ pact, client, stake, cosigners = [], onClose }) {
  if (!pact) return null;
  const weekDate = new Date(pact.week_start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="contract-doc">
          <div className="contract-doc-header">
            <div className="contract-doc-brand">PACT<span>.</span>HEALTH</div>
            <div className="contract-doc-tagline">Binding accountability agreement</div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 22 }}>
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--muted2)', textTransform: 'uppercase', letterSpacing: 0.1 }}>Reference</div>
              <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--navy)', marginTop: 2 }}>{pact.reference}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--muted2)', textTransform: 'uppercase', letterSpacing: 0.1 }}>Week starting</div>
              <div style={{ fontSize: 12, color: 'var(--navy)', marginTop: 2, fontFamily: 'var(--head)', fontWeight: 700 }}>{weekDate}</div>
            </div>
          </div>

          <div className="contract-section">
            <div className="contract-section-title">The parties</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="contract-panel">
                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--muted2)', textTransform: 'uppercase', letterSpacing: 0.1 }}>Athlete</div>
                <div style={{ fontFamily: 'var(--head)', fontSize: 13, fontWeight: 700, color: 'var(--navy)', marginTop: 4 }}>{client?.name}</div>
                {pact.athlete_signed_at && (
                  <div style={{ fontSize: 10, color: 'var(--green)', marginTop: 4, fontWeight: 700 }}>
                    ✓ Signed {new Date(pact.athlete_signed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  </div>
                )}
              </div>
              <div className="contract-panel">
                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--muted2)', textTransform: 'uppercase', letterSpacing: 0.1 }}>Coach</div>
                <div style={{ fontFamily: 'var(--head)', fontSize: 13, fontWeight: 700, color: 'var(--navy)', marginTop: 4 }}>Will Young</div>
                {pact.coach_signed_at && (
                  <div style={{ fontSize: 10, color: 'var(--green)', marginTop: 4, fontWeight: 700 }}>
                    ✓ Signed {new Date(pact.coach_signed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="contract-section">
            <div className="contract-section-title">The commitment</div>
            <div className="contract-panel">
              <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
                {client?.name?.split(' ')[0] || 'Client'} commits to{' '}
                <strong style={{ color: 'var(--navy)' }}>{pact.pacts_committed} training pacts</strong>{' '}
                this week. So far <strong style={{ color: 'var(--navy)' }}>{pact.pacts_kept} kept</strong>.
              </div>
              <div style={{ display: 'flex', gap: 4, marginTop: 12 }}>
                {Array.from({ length: pact.pacts_committed }).map((_, i) => (
                  <div key={i} style={{
                    flex: 1, height: 8, borderRadius: 2,
                    background: i < pact.pacts_kept ? 'var(--navy)' : 'var(--border)',
                  }} />
                ))}
              </div>
            </div>
          </div>

          {stake && (
            <div className="contract-section">
              <div className="contract-section-title">On the line</div>
              <div className="contract-panel" style={{ background: 'var(--red-light)', borderColor: 'rgba(217,45,32,0.2)' }}>
                <div style={{ fontFamily: 'var(--head)', fontSize: 22, fontWeight: 800, color: 'var(--red)' }}>£{stake.amount}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4, fontWeight: 600 }}>
                  {stake.stake_type === 'charity'      && `Forfeit to ${stake.charity_name}`}
                  {stake.stake_type === 'coach_bonus'  && 'Bonus paid to Will'}
                  {stake.stake_type === 'visibility'   && 'Notified to co-signers'}
                  {stake.stake_type === 'streak_bond'  && 'Doubled if all pacts kept this month'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted2)', marginTop: 6 }}>
                  Triggers if {stake.trigger_threshold}+ pacts break this week ·{' '}
                  <strong style={{ color: stake.breaks_so_far > 0 ? 'var(--red)' : 'var(--green)' }}>
                    {stake.breaks_so_far} broken so far
                  </strong>
                </div>
              </div>
            </div>
          )}

          {cosigners.length > 0 && (
            <div className="contract-section">
              <div className="contract-section-title">Witnessed by</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {cosigners.map(c => (
                  <span key={c.id} style={{
                    fontSize: 11, padding: '4px 10px', borderRadius: 4,
                    background: 'var(--bg2)', color: 'var(--navy)',
                    border: '1px solid var(--border)', fontWeight: 600,
                  }}>
                    {c.name} · <span style={{ color: 'var(--muted2)' }}>{c.relationship}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          <button className="btn-primary" style={{ width: '100%', marginTop: 8 }} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════
// ATHLETE APP (Alex's mobile experience)
// ════════════════════════════════════════════════════════════════════════
function AthleteApp({ athleteId, onLogout }) {
  const [tab, setTab] = useState('today');
  const [todayView, setTodayView] = useState('brief'); // brief | recovery
  const [showCeremony, setShowCeremony] = useState(false);
  const [showContract, setShowContract] = useState(false);
  const [showStakes, setShowStakes] = useState(false);

  const athlete = ATHLETES.find(a => a.id === athleteId);
  const data = CLIENT_DATA[athleteId];
  const stake = STAKES[athleteId];
  const cosigners = COSIGNERS[athleteId] || [];
  const allPacts = PACTS[athleteId] || [];
  const activePact = allPacts.find(p => p.status === 'active') || allPacts[allPacts.length - 1];
  const goals = GOALS[athleteId];

  return (
    <div className="athlete-app">
      <div className="athlete-frame">
        <div className="athlete-statusbar">
          <span className="num">7:42</span>
          <span style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 10 }}>
            <span>●●●●</span><span>Wi-Fi</span><span>🔋</span>
          </span>
        </div>

        <div className="athlete-header">
          <div className="athlete-brand">
            <div className="athlete-brand-text">PACT<span>.</span>HEALTH</div>
            <div className="athlete-tag">Athlete</div>
          </div>
          <div className="athlete-header-actions">
            <button className="athlete-icon-btn" onClick={() => setShowCeremony(true)}>📅 Sun</button>
            <button
              className={`athlete-icon-btn ${todayView === 'recovery' ? 'amber' : ''}`}
              onClick={() => { setTab('today'); setTodayView(todayView === 'recovery' ? 'brief' : 'recovery'); }}>
              ↻ Recovery
            </button>
            <button className="athlete-icon-btn" onClick={onLogout} title="Sign out">↪</button>
          </div>
        </div>

        <div className="athlete-scroll">
          {tab === 'today' && todayView === 'brief' && (
            <AthleteToday athlete={athlete} data={data} stake={stake} pact={activePact} onShowContract={() => setShowContract(true)} />
          )}
          {tab === 'today' && todayView === 'recovery' && (
            <AthleteRecovery onContinue={() => setTodayView('brief')} stake={stake} />
          )}
          {tab === 'goals' && (
            <AthleteGoals goals={goals} stake={stake} cosigners={cosigners} pact={activePact} onShowContract={() => setShowContract(true)} onShowStakes={() => setShowStakes(true)} />
          )}
          {tab === 'pacts' && <AthletePacts allPacts={allPacts} />}
          {tab === 'pax' && <AthletePax />}
        </div>

        <div className="a-tabbar">
          <button className={`a-tab ${tab === 'today' ? 'active' : ''}`} onClick={() => setTab('today')}>
            <div className="a-tab-icon">⌂</div>
            <div className="a-tab-label">Today</div>
            {tab === 'today' && <div className="a-tab-active-bar" />}
          </button>
          <button className={`a-tab ${tab === 'goals' ? 'active' : ''}`} onClick={() => setTab('goals')}>
            <div className="a-tab-icon">⊕</div>
            <div className="a-tab-label">Goals</div>
            {tab === 'goals' && <div className="a-tab-active-bar" />}
          </button>
          <button className={`a-tab ${tab === 'pacts' ? 'active' : ''}`} onClick={() => setTab('pacts')}>
            <div className="a-tab-icon">🏆</div>
            <div className="a-tab-label">Pacts</div>
            {tab === 'pacts' && <div className="a-tab-active-bar" />}
          </button>
          <button className={`a-tab ${tab === 'pax' ? 'active' : ''}`} onClick={() => setTab('pax')}>
            <div className="a-tab-icon">💬</div>
            <div className="a-tab-label">PAX</div>
            {tab === 'pax' && <div className="a-tab-active-bar" />}
          </button>
        </div>

        {showCeremony && <SundayCeremony onClose={() => setShowCeremony(false)} />}
        {showContract && activePact && (
          <ContractModal
            pact={activePact} client={athlete} stake={stake} cosigners={cosigners}
            onClose={() => setShowContract(false)}
          />
        )}
        {showStakes && <StakesConfig onClose={() => setShowStakes(false)} />}
      </div>
    </div>
  );
}

// ── Athlete Today (Brief) ─────────────────────────────────────────────
function AthleteToday({ athlete, data, stake, pact, onShowContract }) {
  return (
    <div className="fade-in">
      <div className="a-eyebrow">
        <div className="bar" />
        <div className="text">Tuesday · 06:14 · Pre-flight</div>
      </div>
      <h1 className="a-headline">TODAY'S<br />BRIEF.</h1>
      <p className="a-sub">
        Recovery in. Pact 1 of 4 this week — co-signed Sunday.
      </p>

      {pact && (
        <button onClick={onShowContract} className="a-card" style={{
          marginTop: 12, width: '100%', padding: 10, display: 'flex', alignItems: 'center', gap: 10,
          background: 'var(--white)', border: '1px solid var(--border)', cursor: 'pointer', textAlign: 'left',
        }}>
          <span style={{ fontSize: 16 }}>📜</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)' }}>Today's pact is binding · view contract</div>
            <div style={{ fontSize: 9.5, color: 'var(--muted2)', fontFamily: 'monospace' }}>{pact.reference}{stake && ` · £${stake.amount} stake live`}</div>
          </div>
          <span style={{ color: 'var(--muted2)' }}>→</span>
        </button>
      )}

      <div className="a-grid-2" style={{ marginTop: 12 }}>
        <div className="a-stat-tile">
          <div className="a-stat-label">Recovery</div>
          <div className="a-stat-value" style={{ color: 'var(--green)' }}>78</div>
          <div className="a-stat-sub">HRV +12% · ready</div>
        </div>
        <div className="a-stat-tile">
          <div className="a-stat-label">Sleep</div>
          <div className="a-stat-value">84</div>
          <div className="a-stat-sub">7h 24m</div>
        </div>
      </div>

      <div className="a-card" style={{ marginTop: 8 }}>
        <div className="a-stat-label">Morning weight</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 4 }}>
          <span className="a-stat-value">{athlete.current_weight}</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted2)' }}>kg</span>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 3, background: 'var(--red-light)', color: 'var(--red)' }}>−0.6 wk</span>
        </div>
        <div style={{ fontSize: 10, marginTop: 4, fontWeight: 500, color: 'var(--muted)' }}>
          Target {athlete.target_weight} kg · {(athlete.current_weight - athlete.target_weight).toFixed(1)} to go
        </div>
      </div>

      <div className="a-pax-card" style={{ marginTop: 16 }}>
        <div className="a-pax-card-header">
          <div className="a-pax-avatar"><span>P</span></div>
          <span className="label">PAX briefing</span>
          <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, fontSize: 9, color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>
            <Icons.WhatsApp size={9} color="rgba(255,255,255,0.6)" />Sent at 06:14
          </span>
        </div>
        <div className="a-pax-card-body">{TODAYS_PACT.pax_briefing}</div>
      </div>

      <div style={{ marginTop: 16 }}>
        <div className="a-eyebrow"><div className="bar" /><div className="text">The Pact · {TODAYS_PACT.exercises.length} movements</div></div>
        <div className="a-exercise-list">
          {TODAYS_PACT.exercises.map((ex, i) => (
            <div key={i} className="a-exercise-row">
              <div className={`a-exercise-marker ${ex.type}`} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="a-exercise-name">{ex.name}</div>
                <div className="a-exercise-detail">{ex.detail}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <button className="a-cta" style={{ marginTop: 20 }}>
        🔒 Begin Today's Pact →
      </button>
      <div className="a-cta-sub">
        {stake ? `Stake live · 0 of ${stake.trigger_threshold} broken this week` : 'Locks the plan · timer starts'}
      </div>
    </div>
  );
}

// ── Athlete Recovery (morning after broken pact) ──────────────────────
function AthleteRecovery({ onContinue, stake }) {
  return (
    <div className="fade-in">
      <div style={{
        background: '#fffbeb', border: '1px solid rgba(217,119,6,0.3)',
        borderRadius: 10, padding: 10, fontSize: 11, fontWeight: 600,
        color: 'var(--muted)', marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center',
      }}>
        ☕ Wednesday morning · the day after
      </div>

      <div className="a-eyebrow"><div className="bar" style={{ background: 'var(--amber)' }} /><div className="text" style={{ color: 'var(--amber)' }}>Recovery brief</div></div>
      <h1 className="a-headline">YESTERDAY<br />DIDN'T <span style={{ color: 'var(--amber)' }}>HAPPEN.</span></h1>
      <p className="a-sub">
        That's not a verdict. Pacts break — what matters is the next 24 hours. Three quick things, then we move.
      </p>

      <div className="a-pax-card" style={{ marginTop: 16 }}>
        <div className="a-pax-card-header">
          <div className="a-pax-avatar"><span>P</span></div>
          <span className="label">PAX · No judgment</span>
        </div>
        <div className="a-pax-card-body">
          You missed Tuesday's lower session. Will's seen it — he's not panicking, neither am I. Let's understand what happened and reshape the week so Friday's deadlift doesn't slip too.
        </div>
      </div>

      <div className="a-card" style={{ marginTop: 14 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted2)', textTransform: 'uppercase', letterSpacing: 0.1, marginBottom: 8 }}>Question 1 of 3</div>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)', marginBottom: 12 }}>What got in the way?</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {['Work / overwhelm', 'Genuinely unwell', 'Slept badly', 'Out night before', "Just couldn't", 'Logistics blew up'].map((label, i) => (
            <button key={i} className="athlete-icon-btn" style={{ padding: '8px 10px', fontSize: 10 }}>{label}</button>
          ))}
        </div>
      </div>

      <div className="a-card" style={{ marginTop: 8 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted2)', textTransform: 'uppercase', letterSpacing: 0.1, marginBottom: 8 }}>Question 2 of 3</div>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)', marginBottom: 12 }}>Anything salvageable today?</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[
            { label: '20-min walk at lunch', meta: 'Counts as movement', rec: true },
            { label: 'Light home session tonight', meta: 'Bodyweight only · 25 min' },
            { label: 'Move Tuesday pact to Wednesday', meta: 'Will gets notified', rec: true },
            { label: 'Skip · refocus on Thursday', meta: 'Honest call' },
          ].map((opt, i) => (
            <button key={i} style={{
              padding: 10, borderRadius: 6, textAlign: 'left',
              background: opt.rec ? 'rgba(217,45,32,0.05)' : 'var(--bg)',
              border: `1px solid ${opt.rec ? 'rgba(217,45,32,0.2)' : 'var(--border)'}`,
              cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center',
            }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)' }}>{opt.label}</div>
                <div style={{ fontSize: 10, color: 'var(--muted2)' }}>{opt.meta}</div>
              </div>
              {opt.rec && <span style={{ fontSize: 9, fontWeight: 700, color: 'white', background: 'var(--red)', padding: '2px 6px', borderRadius: 3, textTransform: 'uppercase', letterSpacing: 0.04, flexShrink: 0 }}>PAX picks</span>}
            </button>
          ))}
        </div>
      </div>

      {stake && (
        <div className="a-card" style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 20 }}>💰</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)' }}>Stake unaffected · 1 of {stake.trigger_threshold} trigger</div>
            <div style={{ fontSize: 10, color: 'var(--muted2)' }}>One more break this week and £{stake.amount} forfeit triggers</div>
          </div>
        </div>
      )}

      <button className="a-cta" style={{ marginTop: 16 }} onClick={onContinue}>
        ↻ Re-shape the week
      </button>
    </div>
  );
}


// ── Athlete Goals ─────────────────────────────────────────────────────
function AthleteGoals({ goals, stake, cosigners, pact, onShowContract, onShowStakes }) {
  if (!goals) return <div style={{ padding: 20, color: 'var(--muted2)' }}>No goals set yet.</div>;

  return (
    <div className="fade-in">
      <div className="a-eyebrow"><div className="bar" /><div className="text">The over-arching pact</div></div>
      <h1 className="a-headline">BY <span>MARCH 1ST</span><br />YOU WILL</h1>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
        {goals.targets.map((g, i) => (
          <div key={i} className="a-card">
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
              <div>
                <div className="a-stat-label">{g.metric}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 2 }}>
                  <span className="a-stat-value" style={{ fontSize: 18 }}>{g.current}</span>
                  <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--muted2)' }}>→ {g.target}</span>
                </div>
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 3, background: 'var(--red-light)', color: 'var(--red)' }}>{g.progress}%</span>
            </div>
            <div style={{ height: 4, borderRadius: 2, background: 'var(--border)' }}>
              <div style={{ height: '100%', borderRadius: 2, width: `${g.progress}%`, background: 'var(--red)' }} />
            </div>
          </div>
        ))}
      </div>

      {pact && (
        <button onClick={onShowContract} style={{
          marginTop: 14, width: '100%', padding: 14,
          background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 10,
          display: 'flex', gap: 12, alignItems: 'center', cursor: 'pointer', textAlign: 'left',
        }}>
          <div style={{ width: 36, height: 36, borderRadius: 6, background: 'var(--navy)', color: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>📜</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--navy)' }}>This week's contract</div>
            <div style={{ fontSize: 10, color: 'var(--muted2)', fontFamily: 'monospace' }}>{pact.reference} · co-signed Sunday</div>
          </div>
          <span style={{ color: 'var(--muted2)' }}>→</span>
        </button>
      )}

      {stake && (
        <button onClick={onShowStakes} style={{
          marginTop: 8, width: '100%', padding: 14,
          background: 'var(--white)', border: '1px solid rgba(217,45,32,0.4)', borderRadius: 10,
          display: 'flex', gap: 12, alignItems: 'center', cursor: 'pointer', textAlign: 'left',
        }}>
          <div style={{ width: 36, height: 36, borderRadius: 6, background: 'var(--red)', color: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700 }}>£</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--navy)' }}>£{stake.amount} stake · live</div>
            <div style={{ fontSize: 10, color: 'var(--muted2)' }}>Triggers if {stake.trigger_threshold}+ pacts break this week</div>
          </div>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 3, background: 'var(--green-soft)', color: 'var(--green)' }}>
            {stake.breaks_so_far} of {stake.trigger_threshold}
          </span>
        </button>
      )}

      {cosigners.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div className="a-eyebrow"><div className="bar" /><div className="text">Co-signers · who's watching</div></div>
          <div className="a-card" style={{ padding: 0 }}>
            {cosigners.map((c, i) => (
              <div key={c.id} style={{
                padding: 12, display: 'flex', alignItems: 'center', gap: 12,
                borderTop: i ? '1px solid var(--border)' : 'none',
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 6, flexShrink: 0,
                  background: c.relationship === 'coach' ? 'var(--red)' : 'var(--bg2)',
                  color: c.relationship === 'coach' ? 'white' : 'var(--navy)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'var(--head)', fontWeight: 700, fontSize: 11,
                }}>{initials(c.name)}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--navy)' }}>{c.name}</div>
                  <div style={{ fontSize: 10, color: 'var(--muted2)', textTransform: 'capitalize' }}>{c.relationship}</div>
                </div>
                {c.relationship === 'coach' && <span style={{ color: 'var(--green)', fontSize: 14 }}>✓</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginTop: 16, padding: 14, background: 'var(--navy)', borderRadius: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: 'var(--red)' }}>↻</span>
          <span style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: 0.1 }}>Quarterly amnesty</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontFamily: 'var(--head)', fontWeight: 800, fontSize: 18, color: 'white' }}>1 remaining</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>Resets Jul 1 · use when life genuinely happens</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Athlete Pacts (record) ────────────────────────────────────────────
function AthletePacts({ allPacts }) {
  const totalKept = allPacts.reduce((s, p) => s + p.pacts_kept, 0);
  const totalCommitted = allPacts.reduce((s, p) => s + p.pacts_committed, 0);
  const pct = totalCommitted > 0 ? Math.round((totalKept / totalCommitted) * 100) : 0;

  return (
    <div className="fade-in">
      <div className="a-eyebrow"><div className="bar" /><div className="text">The record · Year-to-date</div></div>
      <h1 className="a-headline">PACTS<br /><span>KEPT.</span></h1>

      <div className="a-grid-2" style={{ marginTop: 16 }}>
        <div className="a-stat-tile" style={{ background: 'var(--navy)', border: 'none' }}>
          <div className="a-stat-label" style={{ color: 'rgba(255,255,255,0.6)' }}>Year</div>
          <div className="a-stat-value" style={{ color: 'white', fontSize: 30 }}>{totalKept}</div>
          <div className="a-stat-sub" style={{ color: 'rgba(255,255,255,0.7)' }}>of {totalCommitted} · {pct}%</div>
        </div>
        <div className="a-stat-tile" style={{ background: 'var(--red)', border: 'none' }}>
          <div className="a-stat-label" style={{ color: 'rgba(255,255,255,0.7)' }}>Streak</div>
          <div className="a-stat-value" style={{ color: 'white', fontSize: 30 }}>23</div>
          <div className="a-stat-sub" style={{ color: 'rgba(255,255,255,0.85)' }}>days · personal best</div>
        </div>
      </div>

      <div className="a-card" style={{ marginTop: 12, border: '2px solid rgba(217,45,32,0.3)' }}>
        <div className="a-eyebrow"><div className="bar" /><div className="text">Stakes record</div></div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <div>
            <div className="a-stat-label">Protected</div>
            <div className="a-stat-value" style={{ fontSize: 18, color: 'var(--green)' }}>£420</div>
            <div className="a-stat-sub">14 weeks safe</div>
          </div>
          <div>
            <div className="a-stat-label">Forfeited</div>
            <div className="a-stat-value" style={{ fontSize: 18, color: 'var(--red)' }}>£40</div>
            <div className="a-stat-sub">2 weeks broken</div>
          </div>
          <div>
            <div className="a-stat-label">Amnesty</div>
            <div className="a-stat-value" style={{ fontSize: 18 }}>1/1</div>
            <div className="a-stat-sub">Q2 remaining</div>
          </div>
        </div>
      </div>

      <div className="a-card" style={{ marginTop: 12 }}>
        <div className="a-eyebrow"><div className="bar" /><div className="text">Recent weeks</div></div>
        {allPacts.slice().reverse().slice(0, 6).map(pact => {
          const wDate = new Date(pact.week_start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
          const pct = pact.pacts_committed > 0 ? (pact.pacts_kept / pact.pacts_committed) * 100 : 0;
          return (
            <div key={pact.id} className="pact-row" style={{ padding: '10px 0' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="pact-row-week">Week of {wDate}</div>
                <div className="pact-row-ref">{pact.reference}</div>
              </div>
              <div className="pact-row-progress">
                <div className="pact-row-bar">
                  <div className="pact-row-fill" style={{
                    width: `${pct}%`,
                    background: pact.status === 'kept' ? 'var(--green)' : pact.status === 'broken' ? 'var(--red)' :
                      pact.status === 'partial' ? 'var(--amber)' : 'var(--navy)'
                  }} />
                </div>
                <span style={{ fontSize: 10, color: 'var(--muted2)', fontFamily: 'var(--head)', fontWeight: 700 }}>
                  {pact.pacts_kept}/{pact.pacts_committed}
                </span>
              </div>
              <span className={`pact-status ${pact.status}`}>{pact.status}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Athlete PAX chat ──────────────────────────────────────────────────
function AthletePax() {
  return (
    <div className="fade-in" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ paddingBottom: 12, borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 6, background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontFamily: 'var(--head)', fontWeight: 900, color: 'white', fontSize: 18 }}>P</span>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)' }}>PAX</div>
            <div style={{ fontSize: 10.5, color: 'var(--muted2)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--green)' }} />
              Always on · with Will's plan
            </div>
          </div>
        </div>
        <div style={{
          marginTop: 10, padding: '6px 10px', borderRadius: 6,
          background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.25)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Icons.WhatsApp size={12} color="var(--whatsapp)" />
          <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--whatsapp-dark)' }}>
            Synced with WhatsApp · same thread, both surfaces
          </span>
        </div>
      </div>

      <div style={{ flex: 1, paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ textAlign: 'center', fontSize: 9.5, fontWeight: 700, color: 'var(--muted2)', textTransform: 'uppercase', letterSpacing: 0.2, padding: '4px 0' }}>
          Today · Tuesday
        </div>
        {PAX_MESSAGES.map((m, i) => m.from === 'pax' ? (
          <div key={i} style={{ display: 'flex', gap: 8, maxWidth: '85%' }}>
            <div style={{ width: 26, height: 26, borderRadius: 5, background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ fontFamily: 'var(--head)', fontWeight: 900, color: 'white', fontSize: 13 }}>P</span>
            </div>
            <div>
              <div style={{
                padding: '8px 12px', borderRadius: '10px 10px 10px 2px',
                background: 'var(--bg2)', color: 'var(--navy)',
                fontSize: 12.5, lineHeight: 1.55,
              }}>{m.text}</div>
              <div style={{ fontSize: 9, color: 'var(--muted2)', marginTop: 4, marginLeft: 4 }}>{m.time}</div>
            </div>
          </div>
        ) : (
          <div key={i} style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ maxWidth: '80%' }}>
              <div style={{
                padding: '8px 12px', borderRadius: '10px 10px 2px 10px',
                background: 'var(--red)', color: 'white',
                fontSize: 12.5, lineHeight: 1.55,
              }}>{m.text}</div>
              <div style={{ fontSize: 9, color: 'var(--muted2)', marginTop: 4, marginRight: 4, textAlign: 'right' }}>{m.time}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ paddingTop: 8, borderTop: '1px solid var(--border)' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 12px', borderRadius: 6,
          background: 'var(--bg)', border: '1px solid var(--border)',
        }}>
          <input placeholder="Talk to PAX · also lands in WhatsApp"
            style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', fontSize: 12.5, color: 'var(--navy)' }} />
          <button style={{ width: 28, height: 28, borderRadius: 6, background: 'var(--navy)', border: 'none', color: 'white', cursor: 'pointer' }}>🎤</button>
          <button style={{ width: 28, height: 28, borderRadius: 6, background: 'var(--red)', border: 'none', color: 'white', cursor: 'pointer' }}>↑</button>
        </div>
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════
// SUNDAY CEREMONY (8-step pact-setting flow)
// ════════════════════════════════════════════════════════════════════════
function SundayCeremony({ onClose }) {
  const [step, setStep] = useState(0);
  const [pacts, setPacts] = useState([
    { day: 'Tuesday', focus: '', target: '' },
    { day: 'Thursday', focus: '', target: '' },
    { day: 'Saturday', focus: '', target: '' },
    { day: 'Sunday', focus: '', target: '' },
  ]);
  const [stakeOn, setStakeOn] = useState(true);
  const [signed, setSigned] = useState(false);

  const totalSteps = 7;
  const progress = ((step + 1) / totalSteps) * 100;

  const updatePact = (i, field, value) => {
    const next = [...pacts];
    next[i] = { ...next[i], [field]: value };
    setPacts(next);
  };

  const presetTargets = {
    'Lower / Squat': '4×8 squat · +2.5kg',
    'Upper / Push':  '4×8 bench · 3×10 OHP',
    'Upper / Pull':  '4×8 row · 3×AMRAP pull-ups',
    'Conditioning':  '20 min Z2 · finisher',
    'Lower / Deadlift': '4×6 deadlift · accessories',
    'Full body':     '5 movements · 45 min',
  };

  const canAdvance = () => {
    if (step === 0) return true; // intro
    if (step >= 1 && step <= 4) {
      const p = pacts[step - 1];
      return p.focus && p.target;
    }
    if (step === 5) return true; // stake decision
    if (step === 6) return signed;
    return true;
  };

  return (
    <div className="ceremony-overlay">
      <div className="ceremony-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 22, height: 22, borderRadius: 4, background: 'var(--red)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontFamily: 'var(--head)', fontWeight: 900, color: 'white', fontSize: 11 }}>P</span>
          </div>
          <div>
            <div style={{ fontFamily: 'var(--head)', fontWeight: 800, fontSize: 12, color: 'white', letterSpacing: '0.02em' }}>
              SUNDAY CEREMONY
            </div>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
              Set the week ahead
            </div>
          </div>
        </div>
        <button onClick={onClose} style={{
          width: 28, height: 28, borderRadius: 6, background: 'rgba(255,255,255,0.08)',
          border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: 16, cursor: 'pointer', lineHeight: 1, padding: 0,
        }}>×</button>
      </div>

      <div className="ceremony-progress">
        <div className="ceremony-progress-bar">
          {[...Array(totalSteps)].map((_, i) => (
            <div key={i} className={`ceremony-progress-step ${i <= step ? 'done' : ''}`} />
          ))}
        </div>
        <div className="ceremony-progress-text">
          Step {step + 1} of {totalSteps} · {Math.round(progress)}%
        </div>
      </div>

      <div className="ceremony-body">
        {step === 0 && (
          <div className="fade-in">
            <div className="ceremony-headline">
              FOUR <span>PACTS.</span><br />ONE WEEK.
            </div>
            <p className="ceremony-sub">
              Each pact is a binding commitment for one specific session. Co-signed by Will. Witnessed by anyone you choose. Signed before the week begins, not in the moment of struggle.
            </p>
            <div className="ceremony-pact-card">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <span style={{ color: 'var(--red)', fontSize: 14 }}>◆</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'white' }}>Specific, not aspirational</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>"Tuesday lower at 6am" — not "train more this week"</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <span style={{ color: 'var(--red)', fontSize: 14 }}>◆</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'white' }}>Counted, not estimated</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>Either kept or broken — no grey area</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <span style={{ color: 'var(--red)', fontSize: 14 }}>◆</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'white' }}>Stake optional, recommended</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>£20 to a charity you don't like, if 2+ pacts break</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {step >= 1 && step <= 4 && (
          <div className="fade-in">
            <div className="ceremony-headline">
              PACT <span>{step}</span> OF 4
            </div>
            <p className="ceremony-sub">
              Pick the day, focus, and the line that means it's done.
            </p>

            <div className="ceremony-pact-card">
              <div className="ceremony-pact-row">
                <div className="ceremony-pact-label">Day</div>
                <div className="ceremony-pact-value">{pacts[step - 1].day}</div>
              </div>
              <div className="ceremony-pact-row">
                <div className="ceremony-pact-label">Focus</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                  {Object.keys(presetTargets).map(opt => (
                    <button key={opt}
                      onClick={() => {
                        updatePact(step - 1, 'focus', opt);
                        updatePact(step - 1, 'target', presetTargets[opt]);
                      }}
                      style={{
                        padding: '6px 10px', borderRadius: 4, fontSize: 11,
                        background: pacts[step - 1].focus === opt ? 'var(--red)' : 'rgba(255,255,255,0.06)',
                        color: pacts[step - 1].focus === opt ? 'white' : 'rgba(255,255,255,0.7)',
                        border: '1px solid ' + (pacts[step - 1].focus === opt ? 'var(--red)' : 'rgba(255,255,255,0.12)'),
                        cursor: 'pointer', fontWeight: 600, fontFamily: 'var(--font)',
                      }}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
              {pacts[step - 1].target && (
                <div className="ceremony-pact-row">
                  <div className="ceremony-pact-label">The line</div>
                  <div className="ceremony-pact-value">{pacts[step - 1].target}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="fade-in">
            <div className="ceremony-headline">
              STAKE THE <span>WEEK?</span>
            </div>
            <p className="ceremony-sub">
              Optional. Stakes turn pacts from intentions into commitments. Picked from your saved options. Triggers if 2+ pacts break.
            </p>

            <div className={'ceremony-stake-toggle ' + (stakeOn ? 'on' : '')} onClick={() => setStakeOn(!stakeOn)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: stakeOn ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                    {stakeOn ? 'Stake live' : 'No stake this week'}
                  </div>
                  <div style={{ fontFamily: 'var(--head)', fontWeight: 800, fontSize: 22, color: 'white', marginTop: 4 }}>
                    {stakeOn ? '£20 → a charity you oppose' : 'No money on the line'}
                  </div>
                </div>
                <div className={'toggle-switch ' + (stakeOn ? 'on' : '')}>
                  <div className="toggle-knob" />
                </div>
              </div>
              {stakeOn && (
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)', marginTop: 10, lineHeight: 1.5 }}>
                  Triggers if 2+ pacts break this week. Charged automatically Sunday night. Will sees the trigger in real time.
                </div>
              )}
            </div>
          </div>
        )}

        {step === 6 && (
          <div className="fade-in">
            <div className="ceremony-headline">
              SIGN AND <span>SEAL.</span>
            </div>
            <p className="ceremony-sub">
              Final step. Co-signed contract, witnessed, archived. Tap to sign.
            </p>

            <div className="ceremony-pact-card">
              <div style={{ fontSize: 9, fontFamily: 'monospace', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.04em' }}>
                PACT-2026-W19-AJ-7820
              </div>
              <div style={{ fontFamily: 'var(--head)', fontWeight: 800, fontSize: 16, color: 'white', marginTop: 6 }}>
                Alex Joyner · Week of {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 4 }}>
                {pacts.filter(p => p.focus).length} pacts committed · {stakeOn ? '£20 stake live' : 'No stake'} · Co-signed by Will Young
              </div>

              <div style={{ marginTop: 14 }}>
                <div className="ceremony-pact-label" style={{ marginBottom: 4 }}>Your signature</div>
                <div className={'signature-pad ' + (signed ? 'signed' : '')} onClick={() => setSigned(true)}>
                  {signed
                    ? <span className="signature-text commit-stamp">Alex Joyner</span>
                    : <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted2)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Tap to sign</span>}
                </div>
              </div>
            </div>

            {signed && (
              <div style={{
                marginTop: 16, padding: 12, borderRadius: 8,
                background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(5,150,105,0.3)',
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <span style={{ color: 'var(--green)', fontSize: 14 }}>✓</span>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)', lineHeight: 1.5 }}>
                  Will notified · contract sealed · stake armed for the week ahead
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="ceremony-footer">
        {step === 6 && signed ? (
          <button className="a-cta green" onClick={onClose}>
            ◆ Lock the week →
          </button>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            {step > 0 && (
              <button onClick={() => setStep(step - 1)} style={{
                padding: '12px 18px', borderRadius: 6, background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)',
                fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em',
                cursor: 'pointer', fontFamily: 'var(--font)',
              }}>← Back</button>
            )}
            <button
              disabled={!canAdvance()}
              onClick={() => setStep(Math.min(step + 1, totalSteps - 1))}
              style={{
                flex: 1, padding: '12px 18px', borderRadius: 6,
                background: canAdvance() ? 'var(--red)' : 'rgba(255,255,255,0.06)',
                color: canAdvance() ? 'white' : 'rgba(255,255,255,0.4)',
                border: 'none', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em',
                cursor: canAdvance() ? 'pointer' : 'not-allowed', fontFamily: 'var(--font)',
              }}>
              {step === 0 ? 'Begin →' : step === 5 ? 'Continue to signature →' : 'Continue →'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// STAKES CONFIG (athlete-facing config screen)
// ════════════════════════════════════════════════════════════════════════
function StakesConfig({ onClose }) {
  const [stakeType, setStakeType] = useState('charity');
  const [amount, setAmount] = useState(20);

  const types = [
    { id: 'charity',     label: 'Anti-charity forfeit', desc: 'Money goes to a charity whose values you oppose. Maximum sting.' },
    { id: 'coach_bonus', label: 'Coach bonus',          desc: 'Pay Will a bonus if you break. Skin in the game on his side too.' },
    { id: 'visibility',  label: 'Public visibility',    desc: 'Co-signers get notified the moment a pact breaks.' },
    { id: 'streak_bond', label: 'Streak bond',          desc: 'Doubles every clean week. Wipes when you break.' },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--red)', textTransform: 'uppercase', letterSpacing: '0.16em', marginBottom: 4 }}>
              Stakes · configure
            </div>
            <h3 style={{ fontFamily: 'var(--head)', fontWeight: 800, fontSize: 20, color: 'var(--navy)', letterSpacing: '-0.01em', lineHeight: 1.2 }}>
              Pick your skin in the game
            </h3>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
            {types.map(t => (
              <button key={t.id} onClick={() => setStakeType(t.id)} style={{
                padding: 14, textAlign: 'left', borderRadius: 8,
                background: stakeType === t.id ? 'var(--red-light)' : 'var(--bg)',
                border: '1px solid ' + (stakeType === t.id ? 'var(--red)' : 'var(--border)'),
                cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'flex-start',
                fontFamily: 'var(--font)',
              }}>
                <div style={{
                  width: 18, height: 18, borderRadius: '50%', flexShrink: 0, marginTop: 2,
                  border: '2px solid ' + (stakeType === t.id ? 'var(--red)' : 'var(--border2)'),
                  background: stakeType === t.id ? 'var(--red)' : 'transparent',
                  position: 'relative',
                }}>
                  {stakeType === t.id && (
                    <span style={{ position: 'absolute', top: -1, left: 3, color: 'white', fontSize: 11, fontWeight: 800 }}>✓</span>
                  )}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>{t.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted2)', marginTop: 3, lineHeight: 1.5 }}>{t.desc}</div>
                </div>
              </button>
            ))}
          </div>

          <div className="form-field">
            <label className="form-label">Amount per week (£)</label>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              {[10, 20, 50, 100].map(v => (
                <button key={v} onClick={() => setAmount(v)} style={{
                  flex: 1, padding: '8px 0', borderRadius: 6,
                  background: amount === v ? 'var(--navy)' : 'var(--white)',
                  color: amount === v ? 'white' : 'var(--navy)',
                  border: '1px solid ' + (amount === v ? 'var(--navy)' : 'var(--border)'),
                  fontFamily: 'var(--head)', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                }}>£{v}</button>
              ))}
            </div>
          </div>

          <div style={{
            padding: 12, borderRadius: 8, background: 'var(--red-light)',
            border: '1px solid rgba(217,45,32,0.2)',
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--red)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>
              Trigger rule
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>
              <strong style={{ color: 'var(--navy)' }}>£{amount}</strong> forfeits if{' '}
              <strong style={{ color: 'var(--navy)' }}>2 or more pacts break</strong> in a single week.
              Quarterly amnesty still applies.
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={onClose}>Save stake</button>
        </div>
      </div>
    </div>
  );
}
